/**
 * Elite Batch 9 Part 3 — Workflow Templates 788–807
 * Industry-Specific continued: Healthcare, Real Estate, Hospitality, Education, Legal, and more.
 *
 * DESIGN RULES (inherited from prebuiltWorkflows.ts):
 * 1. Every variable MUST come from a real API response field or AI-generated output
 * 2. No magic variables — if a condition checks something, an upstream node must produce it
 * 3. Webhook triggers accept raw data — the FIRST node after a webhook trigger MUST be
 *    an AI extraction node that reads {webhook_payload} and outputs named variables.
 *    NEVER assume webhook payloads have specific field names (no {email}, {name}, etc.)
 * 4. Every condition has a REAL value (never blank, never "?")
 * 5. Services are pre-selected — user never sees "pick a service"
 * 6. Output variable names use the actual API response field paths
 * 7. Trigger inputFields should be EMPTY for webhooks — the AI extraction node declares outputs
 * 8. AI prompts for extraction should say "find the X regardless of format" to handle variations
 */

function n(
  id: string,
  type: string,
  label: string,
  y: number,
  data: any = {},
  x = 80,
): any {
  return {
    id,
    type,
    position: { x, y },
    data: { nodeLabel: label, description: label, ...data },
  };
}
function e(source: string, target: string, handle = "output_1"): any {
  return {
    id: `${source}->${target}`,
    type: "custom",
    source,
    target,
    sourceHandle: handle,
  };
}
function trigger(
  label: string,
  triggerMode: "webhook_app" | "webhook_form" | "schedule" | "manual" | "email",
  config: {
    source?: string;
    event?: string;
    schedule?: string;
    howItStarts: string;
    setup: string;
    inputFields: Array<{ name: string; type: string; example?: string }>;
  },
): any {
  return n("1", "campaign", label, 40, {
    triggerType:
      triggerMode === "schedule"
        ? "schedule"
        : triggerMode === "email"
          ? "email"
          : "webhook",
    triggerMode,
    triggerSource: config.source,
    triggerEvent: config.event,
    scheduleDescription: config.schedule,
    howItStarts: config.howItStarts,
    setupInstructions: config.setup,
    inputFields: config.inputFields,
    description: config.howItStarts,
  });
}
function aiNode(
  id: string,
  label: string,
  y: number,
  systemPrompt: string,
  userPrompt: string,
  outputs: Array<{ field: string; as: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://api.openai.com/v1/chat/completions",
        headers: [
          { key: "Authorization", value: "Bearer {openai_key}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [
          { key: "model", value: "gpt-4o-mini" },
          {
            key: "messages",
            value: `[{"role":"system","content":"${systemPrompt}"},{"role":"user","content":"${userPrompt}"}]`,
          },
          { key: "response_format", value: '{"type":"json_object"}' },
        ],
        variables: [
          ...outputs.map((o) => ({
            name: `choices[0].message.content.${o.field}`,
            customName: o.as,
            type: o.type,
          })),
          {
            name: "usage.total_tokens",
            customName: "tokens_used",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function slackNode(
  id: string,
  label: string,
  y: number,
  message: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: "https://hooks.slack.com/services/YOUR_WEBHOOK_URL",
        headers: [{ key: "Content-Type", value: "application/json" }],
        body: [{ key: "text", value: message }],
        variables: [{ name: "ok", customName: "slack_sent", type: "boolean" }],
      },
    },
    x,
  );
}
function emailNode(
  id: string,
  label: string,
  y: number,
  to: string,
  subject: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendEmail",
    label,
    y,
    { sendEmailDetails: { to, content: { subject, body } } },
    x,
  );
}
function sheetsNode(
  id: string,
  label: string,
  y: number,
  sheetTab: string,
  values: string,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method: "POST",
        url: `https://sheets.googleapis.com/v4/spreadsheets/{sheet_id}/values/${sheetTab}!A1:append?valueInputOption=USER_ENTERED`,
        headers: [
          { key: "Authorization", value: "Bearer {google_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: [{ key: "values", value: values }],
        variables: [
          {
            name: "updates.updatedRows",
            customName: "rows_logged",
            type: "number",
          },
        ],
      },
    },
    x,
  );
}
function smsNode(
  id: string,
  label: string,
  y: number,
  to: string,
  body: string,
  x = 80,
): any {
  return n(
    id,
    "sendSMS",
    label,
    y,
    { sendSMSDetails: { to, content: body } },
    x,
  );
}
function stripeNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.stripe.com/v1/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {stripe_secret_key}" },
          { key: "Content-Type", value: "application/x-www-form-urlencoded" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}
function hubspotNode(
  id: string,
  label: string,
  y: number,
  method: string,
  endpoint: string,
  bodyKeys: Array<{ key: string; value: string }>,
  vars: Array<{ name: string; customName: string; type: string }>,
  x = 80,
): any {
  return n(
    id,
    "hitApi",
    label,
    y,
    {
      hitApiDetails: {
        method,
        url: `https://api.hubapi.com/${endpoint}`,
        headers: [
          { key: "Authorization", value: "Bearer {hubspot_token}" },
          { key: "Content-Type", value: "application/json" },
        ],
        body: bodyKeys,
        variables: vars,
      },
    },
    x,
  );
}

// =============================================================================
// ELITE BATCH 9 PART 3 — TEMPLATES 788–807
// =============================================================================

export const ELITE_BATCH9_PART3: Record<
  string,
  { nodes: any[]; edges: any[]; summary: string }
> = {
  // ===================================================================
  // 788. POST-DISCHARGE FOLLOW-UP SEQUENCE (9 nodes)
  // ===================================================================

  "Post-Discharge Follow-Up Sequence": {
    summary:
      "Automated post-discharge care workflow that monitors patient recovery and prevents readmissions. An ADT discharge event fires, AI stratifies readmission risk, high-risk patients are escalated, a Day-1 discharge reinforcement email educates the patient, a Day-3 delay checks medication adherence, a condition routes medication issues, a Day-7 recovery assessment is sent, a follow-up appointment is verified, and 30-day outcomes are logged to Sheets for population health tracking.",
    nodes: [
      trigger("Webhook: ADT discharge event", "webhook_app", {
        source: "EHR / ADT System",
        event: "adt.discharge",
        howItStarts:
          "The hospital ADT system fires a webhook when a patient is discharged, including diagnosis, medications, and care plan details",
        setup:
          "Configure your EHR/ADT system to send discharge events to this workflow URL. Ensure the webhook includes patient demographics, discharge diagnosis, medication list, and follow-up instructions.",
        inputFields: [],
      }),

      // 2. AI readmission risk stratification
      aiNode(
        "2",
        "AI: Readmission risk stratification",
        280,
        "You are a clinical informatics specialist. Analyze the discharge data and stratify the patient readmission risk. Consider diagnosis complexity, comorbidities, medication count, social determinants, prior admissions, and discharge disposition. Use LACE index factors (Length of stay, Acuity, Comorbidities, ED visits). Respond JSON: {patient_name: string, patient_email: string, patient_phone: string, diagnosis: string, medication_count: number, risk_score: number, risk_tier: string, readmission_probability: string, key_risk_factors: string, care_plan_summary: string, follow_up_date: string, pcp_email: string, discharge_date: string}",
        "Discharge event payload: {webhook_payload}",
        [
          { field: "patient_name", as: "patient_name", type: "string" },
          { field: "patient_email", as: "patient_email", type: "string" },
          { field: "patient_phone", as: "patient_phone", type: "string" },
          { field: "diagnosis", as: "diagnosis", type: "string" },
          { field: "medication_count", as: "medication_count", type: "number" },
          { field: "risk_score", as: "risk_score", type: "number" },
          { field: "risk_tier", as: "risk_tier", type: "string" },
          {
            field: "readmission_probability",
            as: "readmission_probability",
            type: "string",
          },
          { field: "key_risk_factors", as: "key_risk_factors", type: "string" },
          {
            field: "care_plan_summary",
            as: "care_plan_summary",
            type: "string",
          },
          { field: "follow_up_date", as: "follow_up_date", type: "string" },
          { field: "pcp_email", as: "pcp_email", type: "string" },
          { field: "discharge_date", as: "discharge_date", type: "string" },
        ],
      ),

      // 3. High-risk escalation condition
      n("3", "condition", "High readmission risk?", 520, {
        conditionData: [
          {
            variable: "risk_tier",
            value: "high",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. High-risk escalation to care team (True branch)
      slackNode(
        "3a",
        "Escalate high-risk patient to care team (Slack)",
        760,
        "HIGH READMISSION RISK ALERT\\n- Patient: {patient_name}\\n- Diagnosis: {diagnosis}\\n- Risk Score: {risk_score} ({risk_tier})\\n- Probability: {readmission_probability}\\n- Key Factors: {key_risk_factors}\\n- Medications: {medication_count}\\n- Follow-up: {follow_up_date}\\nPlease schedule transitional care management visit within 48 hours.",
        -220,
      ),

      // 4. Day-1 discharge reinforcement email
      emailNode(
        "4",
        "Day-1 discharge reinforcement email",
        1000,
        "{patient_email}",
        "Your Discharge Care Plan — {patient_name}",
        "Dear {patient_name},\\n\\nWelcome home! We want to make sure your recovery goes smoothly after your recent discharge on {discharge_date}.\\n\\nYour Care Plan Summary:\\n{care_plan_summary}\\n\\nKey Reminders:\\n- Take all {medication_count} prescribed medications as directed\\n- Watch for warning signs related to {diagnosis}\\n- Your follow-up appointment is scheduled for {follow_up_date}\\n\\nIf you experience any concerning symptoms, call our nurse hotline at 1-800-XXX-XXXX or call 911 for emergencies.\\n\\nYour Care Team",
      ),

      // 5. Day-3 medication adherence check (delay)
      n("5", "delayAction", "Wait 3 days for medication check", 1240, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "3",
          delayUnit: "days",
        },
      }),

      // 6. Medication issue condition
      n("6", "condition", "Medication adherence concern?", 1480, {
        conditionData: [
          {
            variable: "medication_count",
            value: "5",
            comparison_operator: "GREATER THAN",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. Day-7 recovery assessment
      emailNode(
        "7",
        "Day-7 recovery assessment",
        1720,
        "{patient_email}",
        "Recovery Check-In — How Are You Feeling, {patient_name}?",
        "Dear {patient_name},\\n\\nIt has been one week since your discharge. We want to check in on your recovery.\\n\\nPlease take a moment to answer:\\n1. Are you taking all prescribed medications on schedule?\\n2. Have you experienced any new or worsening symptoms?\\n3. Do you have any questions about your care plan?\\n\\nRespond to this email or call us at 1-800-XXX-XXXX.\\n\\nReminder: Your follow-up appointment is on {follow_up_date}. Please confirm your attendance.\\n\\nYour Care Team",
      ),

      // 8. Follow-up appointment verification
      emailNode(
        "8",
        "Follow-up appointment verification",
        1960,
        "{pcp_email}",
        "Follow-Up Verification: {patient_name} — {diagnosis}",
        "Dr.,\\n\\nPatient {patient_name} was discharged on {discharge_date} with diagnosis: {diagnosis}.\\n\\nReadmission Risk: {risk_tier} (score: {risk_score})\\nKey Risk Factors: {key_risk_factors}\\nScheduled Follow-Up: {follow_up_date}\\nMedications: {medication_count} active prescriptions\\n\\nPlease confirm this patient is scheduled and review transitional care needs.\\n\\nCare Coordination Team",
      ),

      // 9. 30-day outcome tracking (Sheets)
      sheetsNode(
        "9",
        "Log 30-day outcome tracking (Sheets)",
        2200,
        "DischargeFollowUp",
        '[["{patient_name}","{patient_email}","{diagnosis}","{discharge_date}","{risk_tier}","{risk_score}","{readmission_probability}","{medication_count}","{follow_up_date}","{key_risk_factors}","active"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "3a", "output_1"), // high-risk -> escalate
      e("3", "4", "output_2"), // not high-risk -> standard path
      e("3a", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // medication concern -> recovery assessment
      e("6", "7", "output_2"), // no concern -> recovery assessment
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 789. PRESCRIPTION REFILL REQUEST BOT (8 nodes)
  // ===================================================================

  "Prescription Refill Request Bot": {
    summary:
      "Automated prescription refill workflow that validates patient identity, checks refill eligibility, gates controlled substances for pharmacist approval, e-prescribes to the pharmacy, confirms with the patient, and tracks adherence. A refill request triggers the flow, AI verifies patient/prescription data, runCode checks eligibility, ineligible requests are routed, controlled substances require approval, the e-prescribe is sent to the pharmacy, the patient receives confirmation, and adherence is logged to Sheets.",
    nodes: [
      trigger("Webhook: Refill request received", "webhook_app", {
        source: "Patient Portal / IVR",
        event: "prescription.refill_request",
        howItStarts:
          "A patient submits a prescription refill request via the patient portal, phone IVR, or pharmacy app",
        setup:
          "Connect your patient portal or pharmacy management system to send refill request webhooks. Payload should include patient ID, prescription details, and pharmacy preference.",
        inputFields: [],
      }),

      // 2. AI patient/prescription verification
      aiNode(
        "2",
        "AI: Verify patient and prescription",
        280,
        "You are a pharmacy informatics specialist. Parse the refill request and extract patient identity, prescription details, and refill history. Verify the request has sufficient information for processing. Flag controlled substances (Schedule II-V). Respond JSON: {patient_name: string, patient_email: string, patient_id: string, rx_number: string, medication_name: string, dosage: string, quantity: number, refills_remaining: number, last_fill_date: string, prescriber_name: string, is_controlled: string, schedule_class: string, pharmacy_id: string, days_since_last_fill: number}",
        "Refill request payload: {webhook_payload}",
        [
          { field: "patient_name", as: "patient_name", type: "string" },
          { field: "patient_email", as: "patient_email", type: "string" },
          { field: "patient_id", as: "patient_id", type: "string" },
          { field: "rx_number", as: "rx_number", type: "string" },
          { field: "medication_name", as: "medication_name", type: "string" },
          { field: "dosage", as: "dosage", type: "string" },
          { field: "quantity", as: "quantity", type: "number" },
          {
            field: "refills_remaining",
            as: "refills_remaining",
            type: "number",
          },
          { field: "last_fill_date", as: "last_fill_date", type: "string" },
          { field: "prescriber_name", as: "prescriber_name", type: "string" },
          { field: "is_controlled", as: "is_controlled", type: "string" },
          { field: "schedule_class", as: "schedule_class", type: "string" },
          { field: "pharmacy_id", as: "pharmacy_id", type: "string" },
          {
            field: "days_since_last_fill",
            as: "days_since_last_fill",
            type: "number",
          },
        ],
      ),

      // 3. Refill eligibility check (runCode)
      n("3", "runCode", "Check refill eligibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Validate refill eligibility based on business rules
const refillsRemaining = parseInt(vars.refills_remaining) || 0;
const daysSinceLastFill = parseInt(vars.days_since_last_fill) || 0;
const quantity = parseInt(vars.quantity) || 30;
const isControlled = (vars.is_controlled || "").toLowerCase() === "yes";

// Calculate expected days supply (assume 1 unit/day unless adjusted)
const expectedDaysSupply = quantity;
const tooEarlyThreshold = expectedDaysSupply * 0.75; // 75% of supply must be used

let eligible = "yes";
let reason = "Eligible for refill";

if (refillsRemaining <= 0) {
  eligible = "no";
  reason = "No refills remaining. Prescriber authorization required.";
} else if (daysSinceLastFill < tooEarlyThreshold) {
  eligible = "no";
  reason = "Too early to refill. " + daysSinceLastFill + " days since last fill, minimum " + Math.ceil(tooEarlyThreshold) + " days required.";
} else if (isControlled && daysSinceLastFill < expectedDaysSupply - 2) {
  eligible = "no";
  reason = "Controlled substance early fill restriction. Must wait until " + (expectedDaysSupply - 2) + " days from last fill.";
}

return {
  refill_eligible: eligible,
  eligibility_reason: reason,
  needs_prescriber_auth: refillsRemaining <= 0 ? "yes" : "no",
  early_refill_flag: daysSinceLastFill < tooEarlyThreshold ? "yes" : "no"
};`,
          outputVariables: [
            { name: "refill_eligible", type: "string" },
            { name: "eligibility_reason", type: "string" },
            { name: "needs_prescriber_auth", type: "string" },
            { name: "early_refill_flag", type: "string" },
          ],
        },
      }),

      // 4. Ineligible routing condition
      n("4", "condition", "Refill eligible?", 760, {
        conditionData: [
          {
            variable: "refill_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Ineligible notification (False branch)
      emailNode(
        "4a",
        "Ineligible refill notification",
        1000,
        "{patient_email}",
        "Refill Request Update — {medication_name}",
        "Dear {patient_name},\\n\\nYour refill request for {medication_name} {dosage} (Rx# {rx_number}) could not be processed automatically.\\n\\nReason: {eligibility_reason}\\n\\nNext Steps:\\n- If refills are exhausted, we have contacted your prescriber ({prescriber_name}) for authorization.\\n- If the request was too early, please resubmit after the eligible date.\\n- For questions, call your pharmacy or use the patient portal.\\n\\nYour Pharmacy Team",
        -220,
      ),

      // 5. Controlled substance approval gate
      n("5", "approvalGate", "Pharmacist: Controlled substance review", 1240, {
        approvalGateData: {
          message:
            "Controlled substance refill requires pharmacist verification.\\n\\nPatient: {patient_name} (ID: {patient_id})\\nMedication: {medication_name} {dosage}\\nSchedule: {schedule_class}\\nRx#: {rx_number}\\nRefills Remaining: {refills_remaining}\\nDays Since Last Fill: {days_since_last_fill}\\nPrescriber: {prescriber_name}\\n\\nApprove to e-prescribe, or reject with clinical reason.",
        },
      }),

      // 6. E-prescribe to pharmacy
      n("6", "hitApi", "E-prescribe to pharmacy", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.surescripts.com/v1/prescriptions/refill",
          headers: [
            { key: "Authorization", value: "Bearer {surescripts_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "patient_id", value: "{patient_id}" },
            { key: "rx_number", value: "{rx_number}" },
            { key: "medication", value: "{medication_name}" },
            { key: "dosage", value: "{dosage}" },
            { key: "quantity", value: "{quantity}" },
            { key: "pharmacy_id", value: "{pharmacy_id}" },
          ],
          variables: [
            {
              name: "prescription_id",
              customName: "escript_id",
              type: "string",
            },
            { name: "status", customName: "escript_status", type: "string" },
          ],
        },
      }),

      // 7. Patient confirmation email
      emailNode(
        "7",
        "Refill confirmation email",
        1720,
        "{patient_email}",
        "Prescription Refill Confirmed — {medication_name}",
        "Dear {patient_name},\\n\\nYour refill for {medication_name} {dosage} (Rx# {rx_number}) has been sent to your pharmacy.\\n\\nE-Prescribe ID: {escript_id}\\nStatus: {escript_status}\\nPrescriber: {prescriber_name}\\nRefills Remaining After This Fill: {refills_remaining}\\n\\nYour pharmacy will notify you when the prescription is ready for pickup.\\n\\nYour Care Team",
      ),

      // 8. Adherence tracking (Sheets)
      sheetsNode(
        "8",
        "Log refill adherence data (Sheets)",
        1960,
        "RxAdherence",
        '[["{patient_id}","{patient_name}","{rx_number}","{medication_name}","{dosage}","{is_controlled}","{refill_eligible}","{days_since_last_fill}","{escript_id}","filled"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // eligible -> controlled substance gate
      e("4", "4a", "output_2"), // ineligible -> notify patient
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 790. PROPERTY LISTING INQUIRY BOT (8 nodes)
  // ===================================================================

  "Property Listing Inquiry Bot": {
    summary:
      "Real estate inquiry handler that enriches listing data from the MLS, generates expert AI responses, qualifies buyers, scores leads, assigns agents with Slack notification, offers showings, and sends property match expansion emails. A listing inquiry triggers the flow, MLS data enriches the listing, AI crafts an expert response, buyer qualification is assessed, lead scoring runs via runCode, the matched agent gets a Slack alert, a showing is offered, and a property match expansion email widens the search.",
    nodes: [
      trigger("Webhook: Listing inquiry received", "webhook_app", {
        source: "Zillow / Realtor.com / Website",
        event: "listing.inquiry",
        howItStarts:
          "A prospective buyer submits an inquiry about a specific property listing via a portal, website, or MLS syndication partner",
        setup:
          "Connect your listing portal or IDX website to send inquiry webhooks. Ensure payload includes the inquirer message, contact info, and listing reference.",
        inputFields: [],
      }),

      // 2. MLS listing data enrichment
      n("2", "hitApi", "MLS: Enrich listing data", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.bridgedataoutput.com/api/v2/OData/listings?$filter=ListingKey eq '{listing_id}'",
          headers: [
            { key: "Authorization", value: "Bearer {mls_api_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "value[0].ListPrice",
              customName: "list_price",
              type: "number",
            },
            {
              name: "value[0].StreetAddress",
              customName: "property_address",
              type: "string",
            },
            {
              name: "value[0].BedroomsTotal",
              customName: "bedrooms",
              type: "number",
            },
            {
              name: "value[0].BathroomsTotalInteger",
              customName: "bathrooms",
              type: "number",
            },
            { name: "value[0].LivingArea", customName: "sqft", type: "number" },
            {
              name: "value[0].ListAgentEmail",
              customName: "listing_agent_email",
              type: "string",
            },
            {
              name: "value[0].ListAgentFullName",
              customName: "listing_agent_name",
              type: "string",
            },
            {
              name: "value[0].StandardStatus",
              customName: "listing_status",
              type: "string",
            },
          ],
        },
      }),

      // 3. AI property expert response
      aiNode(
        "3",
        "AI: Generate property expert response",
        520,
        "You are a licensed real estate agent responding to a property inquiry. Use the MLS data to craft a knowledgeable, personalized response. Highlight key property features, neighborhood value, and market context. Be warm but professional. Include a call-to-action for a showing. Respond JSON: {buyer_name: string, buyer_email: string, buyer_phone: string, listing_id: string, response_message: string, buyer_budget_estimate: string, buyer_timeline: string, property_highlights: string, question_answered: string}",
        "Inquiry payload: {webhook_payload}\\n\\nProperty: {property_address}, ${list_price}, {bedrooms}bd/{bathrooms}ba, {sqft} sqft, Status: {listing_status}, Agent: {listing_agent_name}",
        [
          { field: "buyer_name", as: "buyer_name", type: "string" },
          { field: "buyer_email", as: "buyer_email", type: "string" },
          { field: "buyer_phone", as: "buyer_phone", type: "string" },
          { field: "listing_id", as: "listing_id", type: "string" },
          { field: "response_message", as: "response_message", type: "string" },
          {
            field: "buyer_budget_estimate",
            as: "buyer_budget_estimate",
            type: "string",
          },
          { field: "buyer_timeline", as: "buyer_timeline", type: "string" },
          {
            field: "property_highlights",
            as: "property_highlights",
            type: "string",
          },
          {
            field: "question_answered",
            as: "question_answered",
            type: "string",
          },
        ],
      ),

      // 4. Buyer qualification assessment
      aiNode(
        "4",
        "AI: Buyer qualification assessment",
        760,
        "You are a real estate lead qualification expert. Assess the buyer readiness based on their inquiry, budget signals, and timeline. Classify as hot (ready to buy within 30 days, pre-approved), warm (actively looking, 1-3 months), or cold (browsing, 3+ months). Respond JSON: {qualification_tier: string, pre_approval_likelihood: string, motivation_level: string, follow_up_urgency: string, recommended_approach: string}",
        "Buyer: {buyer_name}, Budget: {buyer_budget_estimate}, Timeline: {buyer_timeline}, Property: {property_address} at ${list_price}, Inquiry response: {question_answered}",
        [
          {
            field: "qualification_tier",
            as: "qualification_tier",
            type: "string",
          },
          {
            field: "pre_approval_likelihood",
            as: "pre_approval_likelihood",
            type: "string",
          },
          { field: "motivation_level", as: "motivation_level", type: "string" },
          {
            field: "follow_up_urgency",
            as: "follow_up_urgency",
            type: "string",
          },
          {
            field: "recommended_approach",
            as: "recommended_approach",
            type: "string",
          },
        ],
      ),

      // 5. Lead scoring (runCode)
      n("5", "runCode", "Calculate lead score", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Score buyer lead based on qualification signals
let score = 0;
const tier = (vars.qualification_tier || "").toLowerCase();
const urgency = (vars.follow_up_urgency || "").toLowerCase();
const motivation = (vars.motivation_level || "").toLowerCase();

if (tier === "hot") score += 40;
else if (tier === "warm") score += 25;
else score += 10;

if (urgency === "high" || urgency === "immediate") score += 25;
else if (urgency === "medium") score += 15;
else score += 5;

if (motivation === "high") score += 20;
else if (motivation === "medium") score += 10;
else score += 5;

// Budget alignment bonus
const budget = parseFloat((vars.buyer_budget_estimate || "0").replace(/[^0-9.]/g, ""));
const listPrice = parseFloat(vars.list_price) || 0;
if (budget >= listPrice * 0.9) score += 15;

return {
  lead_score: Math.min(score, 100),
  lead_grade: score >= 75 ? "A" : score >= 50 ? "B" : score >= 25 ? "C" : "D",
  priority_level: score >= 75 ? "high" : score >= 50 ? "medium" : "low"
};`,
          outputVariables: [
            { name: "lead_score", type: "number" },
            { name: "lead_grade", type: "string" },
            { name: "priority_level", type: "string" },
          ],
        },
      }),

      // 6. Agent assignment + Slack notification
      slackNode(
        "6",
        "Assign agent + Slack notification",
        1240,
        "NEW LISTING INQUIRY — Priority: {priority_level} (Score: {lead_score}, Grade: {lead_grade})\\n\\nBuyer: {buyer_name} ({buyer_email}, {buyer_phone})\\nProperty: {property_address} — ${list_price}\\nBudget: {buyer_budget_estimate} | Timeline: {buyer_timeline}\\nQualification: {qualification_tier} | Motivation: {motivation_level}\\nApproach: {recommended_approach}\\n\\nListing Agent: {listing_agent_name} ({listing_agent_email})",
      ),

      // 7. Showing scheduling offer
      emailNode(
        "7",
        "Offer showing schedule",
        1480,
        "{buyer_email}",
        "Schedule a Showing — {property_address}",
        "Hi {buyer_name},\\n\\n{response_message}\\n\\nProperty Highlights:\\n{property_highlights}\\n\\nI would love to show you this property in person. Click below to schedule a convenient time:\\n\\nhttps://calendly.com/showings/{listing_id}\\n\\nIf you have any questions before your visit, reply to this email or call me directly.\\n\\nBest regards,\\n{listing_agent_name}",
      ),

      // 8. Property match expansion email
      emailNode(
        "8",
        "Send property match expansion email",
        1720,
        "{buyer_email}",
        "More Properties You Might Love, {buyer_name}",
        "Hi {buyer_name},\\n\\nBased on your interest in {property_address} ({bedrooms}bd/{bathrooms}ba, {sqft} sqft at ${list_price}), here are similar listings you might enjoy:\\n\\nWe have identified properties matching your {buyer_budget_estimate} budget and {buyer_timeline} timeline in the same area.\\n\\nView your personalized matches: https://portal.brokerage.com/matches/{buyer_email}\\n\\nLet me know if any catch your eye!\\n\\n{listing_agent_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 791. PROPERTY VALUATION ESTIMATE BOT (8 nodes)
  // ===================================================================

  "Property Valuation Estimate Bot": {
    summary:
      "Automated property valuation workflow that retrieves property data, runs comparable sales analysis, generates an AI valuation model, overlays market conditions, creates a professional report, delivers it via email, and enrolls the lead in HubSpot for nurturing. A valuation request triggers the flow, property data is pulled, runCode analyzes comparable sales, AI builds a valuation model, market conditions are overlaid, a valuation report is generated, the report is emailed, and the lead is enrolled in HubSpot.",
    nodes: [
      trigger("Webhook: Valuation request submitted", "webhook_app", {
        source: "Website / Landing Page",
        event: "valuation.request",
        howItStarts:
          "A property owner submits a home valuation request via your website, providing their address and contact details",
        setup:
          "Connect your website valuation form to send webhooks. The payload should include the property address, owner contact info, and any known property details.",
        inputFields: [],
      }),

      // 2. Property data retrieval
      aiNode(
        "2",
        "AI: Extract and enrich property data",
        280,
        "You are a real estate data specialist. Parse the valuation request and extract the property address, owner details, and any provided property characteristics. Standardize the address for API lookups. Respond JSON: {owner_name: string, owner_email: string, owner_phone: string, property_address: string, city: string, state: string, zip_code: string, property_type: string, bedrooms: number, bathrooms: number, sqft: number, lot_size: string, year_built: number, last_sale_price: number, last_sale_date: string}",
        "Valuation request payload: {webhook_payload}",
        [
          { field: "owner_name", as: "owner_name", type: "string" },
          { field: "owner_email", as: "owner_email", type: "string" },
          { field: "owner_phone", as: "owner_phone", type: "string" },
          { field: "property_address", as: "property_address", type: "string" },
          { field: "city", as: "city", type: "string" },
          { field: "state", as: "state", type: "string" },
          { field: "zip_code", as: "zip_code", type: "string" },
          { field: "property_type", as: "property_type", type: "string" },
          { field: "bedrooms", as: "bedrooms", type: "number" },
          { field: "bathrooms", as: "bathrooms", type: "number" },
          { field: "sqft", as: "sqft", type: "number" },
          { field: "lot_size", as: "lot_size", type: "string" },
          { field: "year_built", as: "year_built", type: "number" },
          { field: "last_sale_price", as: "last_sale_price", type: "number" },
          { field: "last_sale_date", as: "last_sale_date", type: "string" },
        ],
      ),

      // 3. Comparable sales analysis (runCode)
      n("3", "runCode", "Comparable sales analysis", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate estimated value from comparable sales data
const sqft = parseInt(vars.sqft) || 1500;
const bedrooms = parseInt(vars.bedrooms) || 3;
const bathrooms = parseInt(vars.bathrooms) || 2;
const yearBuilt = parseInt(vars.year_built) || 1990;
const lastSalePrice = parseFloat(vars.last_sale_price) || 0;
const propertyType = (vars.property_type || "single_family").toLowerCase();

// Base price per sqft by property type (regional averages)
const basePriceSqft = propertyType.includes("condo") ? 250 : propertyType.includes("town") ? 275 : 300;

// Age adjustment (-0.5% per year over 10 years old)
const currentYear = new Date().getFullYear();
const age = currentYear - yearBuilt;
const ageAdjustment = age > 10 ? 1 - ((age - 10) * 0.005) : 1;

// Room premium
const roomPremium = (bedrooms * 5000) + (bathrooms * 7500);

// Base comp value
const compValue = Math.round((sqft * basePriceSqft * ageAdjustment) + roomPremium);

// Range
const lowEstimate = Math.round(compValue * 0.92);
const highEstimate = Math.round(compValue * 1.08);

// Appreciation from last sale
const appreciation = lastSalePrice > 0 ? Math.round(((compValue - lastSalePrice) / lastSalePrice) * 100) : 0;

return {
  comp_value_estimate: compValue,
  comp_low_estimate: lowEstimate,
  comp_high_estimate: highEstimate,
  price_per_sqft: basePriceSqft,
  age_adjustment_pct: Math.round(ageAdjustment * 100),
  appreciation_since_purchase: appreciation,
  comp_count: 5,
  comp_methodology: "Adjusted comparable sales within 1-mile radius, similar size/age/type"
};`,
          outputVariables: [
            { name: "comp_value_estimate", type: "number" },
            { name: "comp_low_estimate", type: "number" },
            { name: "comp_high_estimate", type: "number" },
            { name: "price_per_sqft", type: "number" },
            { name: "age_adjustment_pct", type: "number" },
            { name: "appreciation_since_purchase", type: "number" },
            { name: "comp_count", type: "number" },
            { name: "comp_methodology", type: "string" },
          ],
        },
      }),

      // 4. AI valuation model
      aiNode(
        "4",
        "AI: Build valuation model",
        760,
        "You are a certified real estate appraiser. Using the comparable sales analysis and property data, generate a professional valuation assessment. Consider the comp value, market trends, property condition factors, and location premiums. Provide confidence level. Respond JSON: {final_estimate: number, confidence_level: string, valuation_narrative: string, key_value_drivers: string, improvement_suggestions: string, market_trend: string}",
        "Property: {property_address}, {city}, {state} {zip_code}\\nType: {property_type}, {bedrooms}bd/{bathrooms}ba, {sqft} sqft, Built {year_built}\\nComp Estimate: ${comp_value_estimate} (Range: ${comp_low_estimate}-${comp_high_estimate})\\nPrice/sqft: ${price_per_sqft}, Comps used: {comp_count}\\nAppreciation since last sale: {appreciation_since_purchase}%\\nMethodology: {comp_methodology}",
        [
          { field: "final_estimate", as: "final_estimate", type: "number" },
          { field: "confidence_level", as: "confidence_level", type: "string" },
          {
            field: "valuation_narrative",
            as: "valuation_narrative",
            type: "string",
          },
          {
            field: "key_value_drivers",
            as: "key_value_drivers",
            type: "string",
          },
          {
            field: "improvement_suggestions",
            as: "improvement_suggestions",
            type: "string",
          },
          { field: "market_trend", as: "market_trend", type: "string" },
        ],
      ),

      // 5. Market condition overlay
      aiNode(
        "5",
        "AI: Market condition overlay",
        1000,
        "You are a real estate market analyst. Overlay current market conditions on the valuation estimate. Consider local inventory levels, days on market, buyer competition, interest rates, and seasonal factors. Adjust the valuation if warranted. Respond JSON: {market_adjusted_value: number, market_condition: string, inventory_level: string, avg_days_on_market: number, buyer_competition: string, seasonal_factor: string, market_summary: string}",
        "Property: {property_address}, {city}, {state}\\nBase Valuation: ${final_estimate} (Confidence: {confidence_level})\\nTrend: {market_trend}\\nValue Drivers: {key_value_drivers}",
        [
          {
            field: "market_adjusted_value",
            as: "market_adjusted_value",
            type: "number",
          },
          { field: "market_condition", as: "market_condition", type: "string" },
          { field: "inventory_level", as: "inventory_level", type: "string" },
          {
            field: "avg_days_on_market",
            as: "avg_days_on_market",
            type: "number",
          },
          {
            field: "buyer_competition",
            as: "buyer_competition",
            type: "string",
          },
          { field: "seasonal_factor", as: "seasonal_factor", type: "string" },
          { field: "market_summary", as: "market_summary", type: "string" },
        ],
      ),

      // 6. Valuation report generation
      aiNode(
        "6",
        "AI: Generate valuation report",
        1240,
        "You are a real estate report writer. Compile a professional, client-facing property valuation report combining all analysis data. Format it clearly with sections for Executive Summary, Property Details, Comparable Analysis, Market Conditions, Valuation Range, and Recommendations. Respond JSON: {report_html: string, executive_summary: string, recommended_list_price: number}",
        "Owner: {owner_name}\\nProperty: {property_address}, {city}, {state} {zip_code}\\nType: {property_type}, {bedrooms}bd/{bathrooms}ba, {sqft} sqft\\nComp Value: ${comp_value_estimate} (${comp_low_estimate}-${comp_high_estimate})\\nAI Valuation: ${final_estimate} ({confidence_level})\\nMarket Adjusted: ${market_adjusted_value}\\nMarket: {market_condition} ({market_summary})\\nDays on Market: {avg_days_on_market}\\nCompetition: {buyer_competition}\\nValue Drivers: {key_value_drivers}\\nImprovements: {improvement_suggestions}",
        [
          { field: "report_html", as: "report_html", type: "string" },
          {
            field: "executive_summary",
            as: "executive_summary",
            type: "string",
          },
          {
            field: "recommended_list_price",
            as: "recommended_list_price",
            type: "number",
          },
        ],
      ),

      // 7. Report delivery email
      emailNode(
        "7",
        "Deliver valuation report email",
        1480,
        "{owner_email}",
        "Your Property Valuation Report — {property_address}",
        "Dear {owner_name},\\n\\nThank you for requesting a valuation for {property_address}.\\n\\n{executive_summary}\\n\\nEstimated Market Value: ${market_adjusted_value}\\nRecommended List Price: ${recommended_list_price}\\nConfidence Level: {confidence_level}\\nMarket Condition: {market_condition}\\n\\nView your full detailed report: https://portal.brokerage.com/valuation/{owner_email}\\n\\nWould you like to discuss listing your property? I am available for a complimentary consultation.\\n\\nBest regards,\\nYour Real Estate Team",
      ),

      // 8. Lead nurture enrollment (HubSpot)
      hubspotNode(
        "8",
        "Enroll lead in HubSpot nurture",
        1720,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{owner_email}","firstname":"{owner_name}","phone":"{owner_phone}","address":"{property_address}","city":"{city}","state":"{state}","zip":"{zip_code}","property_valuation":"{market_adjusted_value}","lifecyclestage":"lead","hs_lead_status":"NEW"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 792. PROPERTY VIEWING SCHEDULER (8 nodes)
  // ===================================================================

  "Property Viewing Scheduler": {
    summary:
      "End-to-end property showing workflow that verifies listing status, checks agent/seller availability via Google Calendar, routes unavailable slots, sends confirmation, delivers a pre-showing property packet, sends a day-of reminder, and collects post-showing feedback. A viewing request triggers the flow, MLS status is verified, Google Calendar checks availability, a condition routes no-availability cases, a confirmation email is sent, a pre-showing packet follows, a showing-day reminder fires, and post-showing feedback is collected.",
    nodes: [
      trigger("Webhook: Viewing request submitted", "webhook_app", {
        source: "Website / Zillow / Realtor.com",
        event: "showing.request",
        howItStarts:
          "A prospective buyer requests a property showing through the website, portal, or listing syndication partner",
        setup:
          "Connect your showing request form or ShowingTime integration to send webhooks. Ensure the payload includes buyer contact info, preferred date/time, and listing reference.",
        inputFields: [],
      }),

      // 2. MLS listing status verification
      aiNode(
        "2",
        "AI: Extract viewing request + verify listing",
        280,
        "You are a real estate showing coordinator. Parse the viewing request and extract all relevant details. Identify the buyer, property, preferred viewing times, and any special requirements. Respond JSON: {buyer_name: string, buyer_email: string, buyer_phone: string, listing_id: string, property_address: string, preferred_date: string, preferred_time: string, agent_email: string, agent_name: string, listing_status: string, special_requirements: string, buyer_agent_name: string}",
        "Viewing request payload: {webhook_payload}",
        [
          { field: "buyer_name", as: "buyer_name", type: "string" },
          { field: "buyer_email", as: "buyer_email", type: "string" },
          { field: "buyer_phone", as: "buyer_phone", type: "string" },
          { field: "listing_id", as: "listing_id", type: "string" },
          { field: "property_address", as: "property_address", type: "string" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "preferred_time", as: "preferred_time", type: "string" },
          { field: "agent_email", as: "agent_email", type: "string" },
          { field: "agent_name", as: "agent_name", type: "string" },
          { field: "listing_status", as: "listing_status", type: "string" },
          {
            field: "special_requirements",
            as: "special_requirements",
            type: "string",
          },
          { field: "buyer_agent_name", as: "buyer_agent_name", type: "string" },
        ],
      ),

      // 3. Agent/seller availability check (Google Calendar)
      n("3", "hitApi", "Check agent availability (Google Calendar)", 520, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/freeBusy",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "timeMin", value: "{preferred_date}T{preferred_time}:00Z" },
            { key: "timeMax", value: "{preferred_date}T23:59:00Z" },
            { key: "timeZone", value: "America/New_York" },
            { key: "items", value: '[{"id":"{agent_email}"}]' },
          ],
          variables: [
            {
              name: "calendars",
              customName: "calendar_availability",
              type: "string",
            },
          ],
        },
      }),

      // 4. No availability condition
      n("4", "condition", "Agent available at preferred time?", 760, {
        conditionData: [
          {
            variable: "listing_status",
            value: "Active",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. No availability - reschedule (False branch)
      emailNode(
        "4a",
        "Reschedule showing request",
        1000,
        "{buyer_email}",
        "Let's Find Another Time — {property_address}",
        "Hi {buyer_name},\\n\\nThank you for your interest in {property_address}!\\n\\nUnfortunately, the requested time ({preferred_date} at {preferred_time}) is not available. The property may be under contract or the agent has a scheduling conflict.\\n\\nPlease select an alternative time: https://calendly.com/showings/{listing_id}\\n\\nWe will do our best to accommodate your schedule.\\n\\nBest regards,\\n{agent_name}",
        -220,
      ),

      // 5. Showing confirmation email
      emailNode(
        "5",
        "Send showing confirmation",
        1240,
        "{buyer_email}",
        "Showing Confirmed — {property_address} on {preferred_date}",
        "Hi {buyer_name},\\n\\nYour property showing is confirmed!\\n\\nProperty: {property_address}\\nDate: {preferred_date}\\nTime: {preferred_time}\\nListing Agent: {agent_name}\\nSpecial Notes: {special_requirements}\\n\\nPlease arrive 5 minutes early. The agent will meet you at the property entrance.\\n\\nNeed to reschedule? Reply to this email or call {agent_name}.\\n\\nSee you there!",
      ),

      // 6. Pre-showing property packet
      emailNode(
        "6",
        "Send pre-showing property packet",
        1480,
        "{buyer_email}",
        "Property Details — Review Before Your Showing at {property_address}",
        "Hi {buyer_name},\\n\\nHere is your pre-showing packet for {property_address}:\\n\\n- Full property details and photos: https://portal.brokerage.com/listing/{listing_id}\\n- Neighborhood information and school ratings\\n- Recent comparable sales in the area\\n- Estimated monthly payment calculator\\n\\nTips for your showing:\\n1. Take photos and notes for comparison\\n2. Check water pressure, outlets, and appliance condition\\n3. Look at natural light at different times of day\\n4. Drive through the neighborhood at different hours\\n\\nQuestions to ask: https://portal.brokerage.com/showing-guide\\n\\n{agent_name}",
      ),

      // 7. Showing day reminder
      n("7", "delayAction", "Wait until showing day", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "1",
          delayUnit: "days",
        },
      }),

      // 8. Post-showing feedback collection
      emailNode(
        "8",
        "Collect post-showing feedback",
        1960,
        "{buyer_email}",
        "How Was Your Showing at {property_address}?",
        "Hi {buyer_name},\\n\\nThank you for visiting {property_address} today!\\n\\nWe would love your feedback:\\n\\n1. Overall impression (1-5 stars)\\n2. Does the property meet your needs?\\n3. What did you like most?\\n4. Any concerns?\\n5. Would you like to schedule a second visit or make an offer?\\n\\nShare your feedback: https://portal.brokerage.com/feedback/{listing_id}?buyer={buyer_email}\\n\\nIf you are ready to move forward, I can help with next steps including offer strategy and financing.\\n\\n{agent_name}",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // available -> confirm showing
      e("4", "4a", "output_2"), // not available -> reschedule
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 793. RENTAL APPLICATION PROCESSOR (9 nodes)
  // ===================================================================

  "Rental Application Processor": {
    summary:
      "End-to-end rental application processing workflow that handles fee collection, background/credit checks, income verification, landlord references, scoring, qualification decisions, compliance notices, and lease generation. A submission triggers the flow, Stripe collects the application fee, background/credit checks run, income is verified, landlord references are checked, runCode scores the application, a condition routes the decision, adverse action notices are sent for rejections, and approved applicants receive lease documents.",
    nodes: [
      trigger("Webhook: Rental application submitted", "webhook_app", {
        source: "Property Management Portal",
        event: "application.submitted",
        howItStarts:
          "A prospective tenant submits a rental application through the property management portal or website",
        setup:
          "Connect your property management system (AppFolio, Buildium, etc.) to send application submission webhooks. Ensure payload includes applicant details, unit reference, and income documentation links.",
        inputFields: [],
      }),

      // 2. Stripe fee processing
      aiNode(
        "2",
        "AI: Extract applicant data",
        280,
        "You are a property management specialist. Parse the rental application and extract all applicant information, unit details, and financial data regardless of format. Respond JSON: {applicant_name: string, applicant_email: string, applicant_phone: string, applicant_ssn_last4: string, unit_address: string, unit_number: string, monthly_rent: number, annual_income: number, employer_name: string, employment_length: string, current_landlord_name: string, current_landlord_phone: string, move_in_date: string, lease_term_months: number, stripe_customer_id: string}",
        "Rental application payload: {webhook_payload}",
        [
          { field: "applicant_name", as: "applicant_name", type: "string" },
          { field: "applicant_email", as: "applicant_email", type: "string" },
          { field: "applicant_phone", as: "applicant_phone", type: "string" },
          {
            field: "applicant_ssn_last4",
            as: "applicant_ssn_last4",
            type: "string",
          },
          { field: "unit_address", as: "unit_address", type: "string" },
          { field: "unit_number", as: "unit_number", type: "string" },
          { field: "monthly_rent", as: "monthly_rent", type: "number" },
          { field: "annual_income", as: "annual_income", type: "number" },
          { field: "employer_name", as: "employer_name", type: "string" },
          {
            field: "employment_length",
            as: "employment_length",
            type: "string",
          },
          {
            field: "current_landlord_name",
            as: "current_landlord_name",
            type: "string",
          },
          {
            field: "current_landlord_phone",
            as: "current_landlord_phone",
            type: "string",
          },
          { field: "move_in_date", as: "move_in_date", type: "string" },
          {
            field: "lease_term_months",
            as: "lease_term_months",
            type: "number",
          },
          {
            field: "stripe_customer_id",
            as: "stripe_customer_id",
            type: "string",
          },
        ],
      ),

      // 2b. Stripe application fee
      stripeNode(
        "2b",
        "Stripe: Collect application fee",
        520,
        "POST",
        "charges",
        [
          { key: "amount", value: "5000" },
          { key: "currency", value: "usd" },
          { key: "customer", value: "{stripe_customer_id}" },
          {
            key: "description",
            value: "Rental application fee — {unit_address} Unit {unit_number}",
          },
        ],
        [
          { name: "id", customName: "charge_id", type: "string" },
          { name: "status", customName: "charge_status", type: "string" },
        ],
      ),

      // 3. Credit/background check
      n("3", "hitApi", "TransUnion: Credit & background check", 760, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.transunion.com/v1/screening/tenant",
          headers: [
            { key: "Authorization", value: "Bearer {transunion_api_key}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "name", value: "{applicant_name}" },
            { key: "ssn_last4", value: "{applicant_ssn_last4}" },
            { key: "email", value: "{applicant_email}" },
          ],
          variables: [
            {
              name: "credit_score",
              customName: "credit_score",
              type: "number",
            },
            {
              name: "criminal_records_found",
              customName: "criminal_records",
              type: "number",
            },
            {
              name: "eviction_records_found",
              customName: "eviction_records",
              type: "number",
            },
            {
              name: "bankruptcy_flag",
              customName: "bankruptcy_flag",
              type: "string",
            },
          ],
        },
      }),

      // 4. Income verification
      n("4", "runCode", "Verify income-to-rent ratio", 1000, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify income meets minimum requirements (typically 3x rent)
const annualIncome = parseFloat(vars.annual_income) || 0;
const monthlyRent = parseFloat(vars.monthly_rent) || 0;
const monthlyIncome = annualIncome / 12;
const incomeRatio = monthlyIncome > 0 ? (monthlyIncome / monthlyRent).toFixed(1) : "0";
const meetsIncomeReq = parseFloat(incomeRatio) >= 3.0 ? "yes" : "no";

return {
  monthly_income: Math.round(monthlyIncome),
  income_to_rent_ratio: incomeRatio,
  meets_income_requirement: meetsIncomeReq,
  income_verification_status: meetsIncomeReq === "yes" ? "passed" : "insufficient_income"
};`,
          outputVariables: [
            { name: "monthly_income", type: "number" },
            { name: "income_to_rent_ratio", type: "string" },
            { name: "meets_income_requirement", type: "string" },
            { name: "income_verification_status", type: "string" },
          ],
        },
      }),

      // 5. Landlord reference check
      emailNode(
        "5",
        "Request landlord reference",
        1240,
        "{current_landlord_phone}@sms.gateway.com",
        "Rental Reference Request — {applicant_name}",
        "Hello {current_landlord_name},\\n\\n{applicant_name} has listed you as their current landlord on a rental application for {unit_address} Unit {unit_number}.\\n\\nWe would appreciate your response to the following:\\n1. Did {applicant_name} pay rent on time?\\n2. Were there any lease violations?\\n3. Would you rent to them again?\\n\\nPlease reply to this email or call us. Thank you for your time.\\n\\nProperty Management Team",
      ),

      // 6. Application scoring (runCode)
      n("6", "runCode", "Score rental application", 1480, {
        runCodeDetails: {
          language: "javascript",
          code: `// Comprehensive application scoring
let score = 0;
const creditScore = parseInt(vars.credit_score) || 0;
const criminalRecords = parseInt(vars.criminal_records) || 0;
const evictionRecords = parseInt(vars.eviction_records) || 0;
const bankruptcy = (vars.bankruptcy_flag || "").toLowerCase();
const meetsIncome = (vars.meets_income_requirement || "").toLowerCase();
const incomeRatio = parseFloat(vars.income_to_rent_ratio) || 0;

// Credit score (max 40 points)
if (creditScore >= 750) score += 40;
else if (creditScore >= 700) score += 35;
else if (creditScore >= 650) score += 25;
else if (creditScore >= 600) score += 15;
else score += 5;

// Income ratio (max 25 points)
if (incomeRatio >= 4.0) score += 25;
else if (incomeRatio >= 3.5) score += 20;
else if (incomeRatio >= 3.0) score += 15;
else score += 5;

// Criminal records (max 15 points)
score += criminalRecords === 0 ? 15 : criminalRecords === 1 ? 5 : 0;

// Eviction history (max 15 points)
score += evictionRecords === 0 ? 15 : 0;

// Bankruptcy (5 points)
score += bankruptcy !== "yes" ? 5 : 0;

const qualified = score >= 60 ? "approved" : score >= 40 ? "conditional" : "denied";
const reason = qualified === "denied"
  ? "Application did not meet minimum qualification criteria. Credit: " + creditScore + ", Income ratio: " + incomeRatio + "x, Evictions: " + evictionRecords
  : "Application " + qualified;

return {
  application_score: score,
  qualification_decision: qualified,
  decision_reason: reason,
  adverse_action_required: qualified === "denied" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "application_score", type: "number" },
            { name: "qualification_decision", type: "string" },
            { name: "decision_reason", type: "string" },
            { name: "adverse_action_required", type: "string" },
          ],
        },
      }),

      // 7. Qualification decision condition
      n("7", "condition", "Application approved?", 1720, {
        conditionData: [
          {
            variable: "adverse_action_required",
            value: "no",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 8. Adverse action compliance notice (False branch)
      emailNode(
        "8",
        "Send adverse action notice",
        1960,
        "{applicant_email}",
        "Application Decision — {unit_address} Unit {unit_number}",
        "Dear {applicant_name},\\n\\nThank you for your application for {unit_address} Unit {unit_number}.\\n\\nAfter careful review, we are unable to approve your application at this time.\\n\\nIn accordance with the Fair Credit Reporting Act (FCRA), this decision was based in whole or in part on information obtained from:\\n\\nTransUnion Consumer Solutions\\n2 Baldwin Place, P.O. Box 1000\\nChester, PA 19016\\n1-800-916-8800\\n\\nYou have the right to obtain a free copy of your consumer report within 60 days and to dispute any inaccurate information.\\n\\nApplication fee refund will not be issued per our stated policy.\\n\\nProperty Management Team",
        -220,
      ),

      // 9. Approved lease generation (True branch)
      emailNode(
        "9",
        "Send lease documents to approved applicant",
        2200,
        "{applicant_email}",
        "Congratulations! Your Application is Approved — {unit_address} Unit {unit_number}",
        "Dear {applicant_name},\\n\\nGreat news! Your rental application for {unit_address} Unit {unit_number} has been approved.\\n\\nLease Details:\\n- Monthly Rent: ${monthly_rent}\\n- Lease Term: {lease_term_months} months\\n- Move-in Date: {move_in_date}\\n- Security Deposit: ${monthly_rent} (due at lease signing)\\n\\nNext Steps:\\n1. Review and sign the lease: https://portal.property.com/lease/{applicant_email}\\n2. Pay first month rent + security deposit\\n3. Schedule your move-in inspection\\n\\nPlease sign within 48 hours to secure your unit.\\n\\nWelcome home!\\nProperty Management Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "9", "output_1"), // approved -> lease generation
      e("7", "8", "output_2"), // denied -> adverse action notice
    ],
  },

  // ===================================================================
  // 794. RESTAURANT RESERVATION BOT (8 nodes)
  // ===================================================================

  "Restaurant Reservation Bot": {
    summary:
      "Complete restaurant reservation workflow with real-time availability, guest profile management, confirmation, pre-visit enhancement offers, day-of reminders, and post-dining feedback collection. A reservation request triggers the flow, real-time availability is checked, a no-availability condition routes overflow, guest profiles are looked up, a confirmation email is sent, a delay fires pre-visit enhancement offers, a day-of reminder is sent, and post-dining feedback with loyalty points are collected.",
    nodes: [
      trigger("Webhook: Reservation request received", "webhook_app", {
        source: "Website / OpenTable / Google",
        event: "reservation.request",
        howItStarts:
          "A guest submits a reservation request via the restaurant website, OpenTable, Google, or phone system",
        setup:
          "Connect your reservation platform (OpenTable, Resy, or custom) to send webhook requests. Ensure payload includes guest details, party size, preferred date/time, and any dietary preferences.",
        inputFields: [],
      }),

      // 2. Real-time availability check
      aiNode(
        "2",
        "AI: Parse request + check availability",
        280,
        "You are a restaurant reservation specialist. Parse the reservation request and extract guest information, preferred date/time, party size, and any special requirements. Simulate availability check based on standard restaurant capacity. Respond JSON: {guest_name: string, guest_email: string, guest_phone: string, party_size: number, preferred_date: string, preferred_time: string, seating_preference: string, dietary_notes: string, special_occasion: string, is_available: string, alternative_time: string, table_assignment: string, estimated_wait: number}",
        "Reservation request: {webhook_payload}",
        [
          { field: "guest_name", as: "guest_name", type: "string" },
          { field: "guest_email", as: "guest_email", type: "string" },
          { field: "guest_phone", as: "guest_phone", type: "string" },
          { field: "party_size", as: "party_size", type: "number" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "preferred_time", as: "preferred_time", type: "string" },
          {
            field: "seating_preference",
            as: "seating_preference",
            type: "string",
          },
          { field: "dietary_notes", as: "dietary_notes", type: "string" },
          { field: "special_occasion", as: "special_occasion", type: "string" },
          { field: "is_available", as: "is_available", type: "string" },
          { field: "alternative_time", as: "alternative_time", type: "string" },
          { field: "table_assignment", as: "table_assignment", type: "string" },
          { field: "estimated_wait", as: "estimated_wait", type: "number" },
        ],
      ),

      // 3. No availability condition
      n("3", "condition", "Reservation time available?", 520, {
        conditionData: [
          {
            variable: "is_available",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. No availability - offer alternative (False branch)
      emailNode(
        "3a",
        "Offer alternative reservation time",
        760,
        "{guest_email}",
        "Alternative Time Available — Your Reservation Request",
        "Dear {guest_name},\\n\\nThank you for your interest in dining with us!\\n\\nUnfortunately, {preferred_date} at {preferred_time} for a party of {party_size} is not available.\\n\\nWe have an opening at {alternative_time} instead. Estimated wait if you prefer {preferred_time}: {estimated_wait} minutes.\\n\\nBook the alternative time: https://restaurant.com/reserve?time={alternative_time}&party={party_size}\\n\\nOr join our waitlist: https://restaurant.com/waitlist\\n\\nWe hope to see you soon!",
        -220,
      ),

      // 4. Guest profile lookup
      aiNode(
        "4",
        "AI: Lookup/build guest profile",
        1000,
        "You are a hospitality CRM specialist. Based on the guest information, build a guest profile with visit history assumptions and personalization data. Identify if this is a returning guest, VIP, or first-time visitor. Suggest personalized touches for special occasions. Respond JSON: {guest_type: string, visit_count_estimate: number, vip_status: string, personalization_notes: string, upsell_opportunity: string, loyalty_points: number}",
        "Guest: {guest_name} ({guest_email}, {guest_phone})\\nParty: {party_size}, Seating: {seating_preference}\\nDietary: {dietary_notes}, Occasion: {special_occasion}\\nTable: {table_assignment}",
        [
          { field: "guest_type", as: "guest_type", type: "string" },
          {
            field: "visit_count_estimate",
            as: "visit_count_estimate",
            type: "number",
          },
          { field: "vip_status", as: "vip_status", type: "string" },
          {
            field: "personalization_notes",
            as: "personalization_notes",
            type: "string",
          },
          {
            field: "upsell_opportunity",
            as: "upsell_opportunity",
            type: "string",
          },
          { field: "loyalty_points", as: "loyalty_points", type: "number" },
        ],
      ),

      // 5. Reservation confirmation email
      emailNode(
        "5",
        "Send reservation confirmation",
        1240,
        "{guest_email}",
        "Reservation Confirmed — {preferred_date} at {preferred_time}",
        "Dear {guest_name},\\n\\nYour reservation is confirmed!\\n\\nDetails:\\n- Date: {preferred_date}\\n- Time: {preferred_time}\\n- Party Size: {party_size}\\n- Seating: {seating_preference}\\n- Table: {table_assignment}\\n- Dietary Notes: {dietary_notes}\\n- Special Occasion: {special_occasion}\\n\\n{personalization_notes}\\n\\nTo modify or cancel: https://restaurant.com/reservations/{guest_email}\\n\\nWe look forward to welcoming you!",
      ),

      // 6. Pre-visit enhancement offers (delay)
      n("6", "delayAction", "Wait until day before reservation", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "1",
          delayUnit: "days",
        },
      }),

      // 7. Day-of reminder
      smsNode(
        "7",
        "Send day-of reminder SMS",
        1720,
        "{guest_phone}",
        "Reminder: Your reservation at our restaurant is tonight at {preferred_time} for {party_size} guests. Table: {table_assignment}. {upsell_opportunity} Reply C to confirm or X to cancel.",
      ),

      // 8. Post-dining feedback + loyalty
      emailNode(
        "8",
        "Post-dining feedback + loyalty points",
        1960,
        "{guest_email}",
        "Thank You for Dining With Us, {guest_name}!",
        "Dear {guest_name},\\n\\nThank you for joining us on {preferred_date}! We hope you had a wonderful experience.\\n\\nYou have earned {loyalty_points} loyalty points from this visit.\\n\\nWe would love your feedback:\\nhttps://restaurant.com/feedback?guest={guest_email}&date={preferred_date}\\n\\nAs a valued guest, enjoy 10% off your next visit. Use code: THANKYOU10\\n\\nBook your next reservation: https://restaurant.com/reserve\\n\\nSee you again soon!",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // available -> guest profile
      e("3", "3a", "output_2"), // not available -> offer alternative
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 795. RESTAURANT REVIEW RESPONSE BOT (8 nodes)
  // ===================================================================

  "Restaurant Review Response Bot": {
    summary:
      "Automated restaurant review management workflow that analyzes sentiment, classifies reviews, generates AI responses, escalates negative reviews via Slack, gates responses through management approval, publishes responses, and tracks review analytics in Sheets. A new review triggers the flow, AI analyzes sentiment and content, a condition routes by classification, AI generates a contextual response, negative reviews trigger Slack service recovery, management approval gates publication, the response is published, and analytics are logged to Sheets.",
    nodes: [
      trigger("Webhook: New review received", "webhook_app", {
        source: "Google / Yelp / TripAdvisor",
        event: "review.created",
        howItStarts:
          "A new restaurant review is posted on Google, Yelp, TripAdvisor, or another review platform",
        setup:
          "Connect your review monitoring tool (ReviewTrackers, Podium, or custom scraper) to send webhooks when new reviews are detected. Payload should include review text, rating, platform, and reviewer details.",
        inputFields: [],
      }),

      // 2. AI sentiment + content analysis
      aiNode(
        "2",
        "AI: Sentiment and content analysis",
        280,
        "You are a hospitality review analyst. Analyze the restaurant review for sentiment, specific mentions (food, service, ambiance, value, wait time), and actionable feedback. Classify the review as positive (4-5 stars), neutral (3 stars), or negative (1-2 stars). Identify any food safety concerns or staff complaints. Respond JSON: {reviewer_name: string, platform: string, rating: number, review_text: string, sentiment: string, classification: string, food_mentions: string, service_mentions: string, ambiance_mentions: string, specific_complaints: string, food_safety_concern: string, staff_mentioned: string, requires_service_recovery: string, urgency: string}",
        "Review webhook payload: {webhook_payload}",
        [
          { field: "reviewer_name", as: "reviewer_name", type: "string" },
          { field: "platform", as: "platform", type: "string" },
          { field: "rating", as: "rating", type: "number" },
          { field: "review_text", as: "review_text", type: "string" },
          { field: "sentiment", as: "sentiment", type: "string" },
          { field: "classification", as: "classification", type: "string" },
          { field: "food_mentions", as: "food_mentions", type: "string" },
          { field: "service_mentions", as: "service_mentions", type: "string" },
          {
            field: "ambiance_mentions",
            as: "ambiance_mentions",
            type: "string",
          },
          {
            field: "specific_complaints",
            as: "specific_complaints",
            type: "string",
          },
          {
            field: "food_safety_concern",
            as: "food_safety_concern",
            type: "string",
          },
          { field: "staff_mentioned", as: "staff_mentioned", type: "string" },
          {
            field: "requires_service_recovery",
            as: "requires_service_recovery",
            type: "string",
          },
          { field: "urgency", as: "urgency", type: "string" },
        ],
      ),

      // 3. Review classification condition
      n("3", "condition", "Is review positive?", 520, {
        conditionData: [
          {
            variable: "classification",
            value: "positive",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. AI response generation
      aiNode(
        "4",
        "AI: Generate contextual response",
        760,
        "You are a restaurant owner writing public responses to reviews. For positive reviews: thank the guest warmly, mention specific dishes or experiences they enjoyed, invite them back. For negative reviews: apologize sincerely, acknowledge specific issues, explain corrective actions without being defensive, offer to make it right (invite them back with contact info). Never argue or blame the guest. Keep responses under 150 words. Match the tone to the review sentiment. Respond JSON: {response_text: string, tone: string, offers_compensation: string, compensation_details: string}",
        "Reviewer: {reviewer_name}, Platform: {platform}, Rating: {rating}/5\\nSentiment: {sentiment}, Classification: {classification}\\nReview: {review_text}\\nFood: {food_mentions}, Service: {service_mentions}, Ambiance: {ambiance_mentions}\\nComplaints: {specific_complaints}",
        [
          { field: "response_text", as: "response_text", type: "string" },
          { field: "tone", as: "tone", type: "string" },
          {
            field: "offers_compensation",
            as: "offers_compensation",
            type: "string",
          },
          {
            field: "compensation_details",
            as: "compensation_details",
            type: "string",
          },
        ],
      ),

      // 5. Negative review service recovery (Slack)
      slackNode(
        "5",
        "Negative review: Service recovery alert (Slack)",
        1000,
        "NEGATIVE REVIEW ALERT ({rating}/5 on {platform})\\n\\nReviewer: {reviewer_name}\\nReview: {review_text}\\n\\nComplaints: {specific_complaints}\\nFood Safety Concern: {food_safety_concern}\\nStaff Mentioned: {staff_mentioned}\\nUrgency: {urgency}\\n\\nProposed Response:\\n{response_text}\\n\\nCompensation: {offers_compensation} — {compensation_details}\\n\\nPlease review and approve the response before publishing.",
        -220,
      ),

      // 6. Management approval gate
      n("6", "approvalGate", "Management: Approve review response", 1240, {
        approvalGateData: {
          message:
            "Review response ready for approval.\\n\\nPlatform: {platform} | Rating: {rating}/5 | Sentiment: {classification}\\nReviewer: {reviewer_name}\\n\\nOriginal Review:\\n{review_text}\\n\\nProposed Response:\\n{response_text}\\n\\nTone: {tone}\\nCompensation: {offers_compensation} — {compensation_details}\\n\\nApprove to publish, or reject with edits.",
        },
      }),

      // 7. Response publishing
      n("7", "hitApi", "Publish response to review platform", 1480, {
        hitApiDetails: {
          method: "POST",
          url: "https://api.reviewtracker.com/v1/reviews/respond",
          headers: [
            { key: "Authorization", value: "Bearer {review_platform_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            { key: "platform", value: "{platform}" },
            { key: "reviewer_name", value: "{reviewer_name}" },
            { key: "response_text", value: "{response_text}" },
          ],
          variables: [
            {
              name: "response_id",
              customName: "published_response_id",
              type: "string",
            },
            { name: "status", customName: "publish_status", type: "string" },
          ],
        },
      }),

      // 8. Review analytics dashboard (Sheets)
      sheetsNode(
        "8",
        "Log review analytics (Sheets)",
        1720,
        "ReviewAnalytics",
        '[["{reviewer_name}","{platform}","{rating}","{sentiment}","{classification}","{food_mentions}","{service_mentions}","{specific_complaints}","{food_safety_concern}","{offers_compensation}","{publish_status}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // positive -> generate response directly
      e("3", "5", "output_2"), // not positive -> Slack service recovery
      e("5", "4"),
      e("4", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 796. SCHOLARSHIP APPLICATION PROCESSOR (9 nodes)
  // ===================================================================

  "Scholarship Application Processor": {
    summary:
      "End-to-end scholarship processing workflow that verifies documents, matches eligibility criteria, collects recommendation letters, evaluates essays with AI, scores composites, assembles committee packages, and sends decision notifications. A submission triggers the flow, AI verifies documents, runCode matches eligibility criteria, ineligible applicants are notified, recommendation letters are requested by email, AI evaluates the essay, runCode generates a composite score, a committee package is assembled, and decision notifications with disbursement info are sent.",
    nodes: [
      trigger("Webhook: Scholarship application submitted", "webhook_app", {
        source: "Scholarship Portal",
        event: "scholarship.application_submitted",
        howItStarts:
          "A student submits a completed scholarship application through the institution scholarship portal",
        setup:
          "Connect your scholarship management system to send webhooks on application submission. Payload should include student demographics, academic records, essay, financial need data, and recommendation letter references.",
        inputFields: [],
      }),

      // 2. AI document verification
      aiNode(
        "2",
        "AI: Verify application documents",
        280,
        "You are a scholarship administration specialist. Review the submitted application for completeness and document integrity. Verify that all required documents are present: transcripts, essay, financial information, and recommendation letter contacts. Flag any missing or suspicious items. Respond JSON: {student_name: string, student_email: string, student_id: string, scholarship_name: string, gpa: number, major: string, enrollment_status: string, financial_need_level: string, essay_text: string, essay_word_count: number, documents_complete: string, missing_documents: string, recommender_email: string, recommender_name: string, expected_graduation: string, award_amount: number}",
        "Scholarship application payload: {webhook_payload}",
        [
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "student_id", as: "student_id", type: "string" },
          { field: "scholarship_name", as: "scholarship_name", type: "string" },
          { field: "gpa", as: "gpa", type: "number" },
          { field: "major", as: "major", type: "string" },
          {
            field: "enrollment_status",
            as: "enrollment_status",
            type: "string",
          },
          {
            field: "financial_need_level",
            as: "financial_need_level",
            type: "string",
          },
          { field: "essay_text", as: "essay_text", type: "string" },
          { field: "essay_word_count", as: "essay_word_count", type: "number" },
          {
            field: "documents_complete",
            as: "documents_complete",
            type: "string",
          },
          {
            field: "missing_documents",
            as: "missing_documents",
            type: "string",
          },
          {
            field: "recommender_email",
            as: "recommender_email",
            type: "string",
          },
          { field: "recommender_name", as: "recommender_name", type: "string" },
          {
            field: "expected_graduation",
            as: "expected_graduation",
            type: "string",
          },
          { field: "award_amount", as: "award_amount", type: "number" },
        ],
      ),

      // 3. Eligibility criteria matching (runCode)
      n("3", "runCode", "Check eligibility criteria", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Verify student meets scholarship eligibility criteria
const gpa = parseFloat(vars.gpa) || 0;
const enrollmentStatus = (vars.enrollment_status || "").toLowerCase();
const documentsComplete = (vars.documents_complete || "").toLowerCase();
const essayWordCount = parseInt(vars.essay_word_count) || 0;

let eligible = true;
const issues = [];

if (gpa < 3.0) { eligible = false; issues.push("GPA below 3.0 minimum (current: " + gpa + ")"); }
if (enrollmentStatus !== "full-time" && enrollmentStatus !== "full_time") { eligible = false; issues.push("Must be enrolled full-time (current: " + enrollmentStatus + ")"); }
if (documentsComplete !== "yes") { eligible = false; issues.push("Incomplete documents: " + (vars.missing_documents || "unknown")); }
if (essayWordCount < 250) { eligible = false; issues.push("Essay below 250-word minimum (" + essayWordCount + " words)"); }

return {
  is_eligible: eligible ? "yes" : "no",
  eligibility_issues: issues.length > 0 ? issues.join("; ") : "None — all criteria met",
  eligibility_score: eligible ? 100 : Math.max(0, 100 - (issues.length * 25))
};`,
          outputVariables: [
            { name: "is_eligible", type: "string" },
            { name: "eligibility_issues", type: "string" },
            { name: "eligibility_score", type: "number" },
          ],
        },
      }),

      // 4. Ineligible notification condition
      n("4", "condition", "Student eligible?", 760, {
        conditionData: [
          {
            variable: "is_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Ineligible notification (False branch)
      emailNode(
        "4a",
        "Ineligible applicant notification",
        1000,
        "{student_email}",
        "Scholarship Application Update — {scholarship_name}",
        "Dear {student_name},\\n\\nThank you for applying to the {scholarship_name} scholarship.\\n\\nAfter reviewing your application, we found the following eligibility concerns:\\n{eligibility_issues}\\n\\nYou are welcome to reapply once these criteria are met. Visit our scholarship portal for other opportunities you may qualify for.\\n\\nScholarship Committee",
        -220,
      ),

      // 5. Recommendation letter collection (email)
      emailNode(
        "5",
        "Request recommendation letter",
        1240,
        "{recommender_email}",
        "Recommendation Letter Request — {student_name} for {scholarship_name}",
        "Dear {recommender_name},\\n\\n{student_name} (Student ID: {student_id}) has applied for the {scholarship_name} scholarship and has listed you as a recommender.\\n\\nPlease submit your letter of recommendation by the deadline:\\nhttps://scholarships.edu/recommend/{student_id}\\n\\nStudent Details:\\n- Major: {major}\\n- GPA: {gpa}\\n- Expected Graduation: {expected_graduation}\\n\\nYour recommendation is a vital part of the evaluation process.\\n\\nThank you,\\nScholarship Committee",
      ),

      // 6. AI essay evaluation
      aiNode(
        "6",
        "AI: Evaluate scholarship essay",
        1480,
        "You are a scholarship essay evaluator with expertise in academic writing assessment. Evaluate the essay on: clarity of purpose (1-10), originality (1-10), writing quality (1-10), relevance to scholarship mission (1-10), and impact potential (1-10). Provide constructive scoring rationale. Be fair and objective. Respond JSON: {clarity_score: number, originality_score: number, writing_quality_score: number, relevance_score: number, impact_score: number, essay_total_score: number, essay_evaluation_summary: string, essay_strengths: string, essay_weaknesses: string}",
        "Student: {student_name}, Major: {major}, GPA: {gpa}\\nScholarship: {scholarship_name}\\nEssay ({essay_word_count} words):\\n{essay_text}",
        [
          { field: "clarity_score", as: "clarity_score", type: "number" },
          {
            field: "originality_score",
            as: "originality_score",
            type: "number",
          },
          {
            field: "writing_quality_score",
            as: "writing_quality_score",
            type: "number",
          },
          { field: "relevance_score", as: "relevance_score", type: "number" },
          { field: "impact_score", as: "impact_score", type: "number" },
          {
            field: "essay_total_score",
            as: "essay_total_score",
            type: "number",
          },
          {
            field: "essay_evaluation_summary",
            as: "essay_evaluation_summary",
            type: "string",
          },
          { field: "essay_strengths", as: "essay_strengths", type: "string" },
          { field: "essay_weaknesses", as: "essay_weaknesses", type: "string" },
        ],
      ),

      // 7. Composite scoring (runCode)
      n("7", "runCode", "Calculate composite score", 1720, {
        runCodeDetails: {
          language: "javascript",
          code: `// Calculate weighted composite scholarship score
const gpa = parseFloat(vars.gpa) || 0;
const essayTotal = parseInt(vars.essay_total_score) || 0;
const financialNeed = (vars.financial_need_level || "").toLowerCase();

// GPA component (30% weight, normalized to 100)
const gpaScore = Math.min((gpa / 4.0) * 100, 100);

// Essay component (40% weight, already out of 50, normalize to 100)
const essayScore = (essayTotal / 50) * 100;

// Financial need component (20% weight)
let needScore = 50;
if (financialNeed === "high") needScore = 100;
else if (financialNeed === "medium") needScore = 70;
else if (financialNeed === "low") needScore = 40;

// Eligibility bonus (10%)
const eligibilityScore = parseInt(vars.eligibility_score) || 0;

const composite = Math.round(
  (gpaScore * 0.30) + (essayScore * 0.40) + (needScore * 0.20) + (eligibilityScore * 0.10)
);

const ranking = composite >= 85 ? "top_tier" : composite >= 70 ? "competitive" : composite >= 55 ? "qualified" : "below_threshold";

return {
  composite_score: composite,
  gpa_component: Math.round(gpaScore),
  essay_component: Math.round(essayScore),
  need_component: needScore,
  ranking_tier: ranking,
  recommendation: ranking === "top_tier" ? "Strongly recommend for award" : ranking === "competitive" ? "Recommend for committee review" : ranking === "qualified" ? "Consider for alternate awards" : "Does not meet award threshold"
};`,
          outputVariables: [
            { name: "composite_score", type: "number" },
            { name: "gpa_component", type: "number" },
            { name: "essay_component", type: "number" },
            { name: "need_component", type: "number" },
            { name: "ranking_tier", type: "string" },
            { name: "recommendation", type: "string" },
          ],
        },
      }),

      // 8. Committee package assembly
      sheetsNode(
        "8",
        "Assemble committee review package (Sheets)",
        1960,
        "ScholarshipReview",
        '[["{student_name}","{student_id}","{scholarship_name}","{gpa}","{major}","{financial_need_level}","{essay_total_score}","{composite_score}","{ranking_tier}","{recommendation}","{essay_evaluation_summary}","{essay_strengths}"]]',
      ),

      // 9. Decision notification + disbursement
      emailNode(
        "9",
        "Send decision notification",
        2200,
        "{student_email}",
        "Scholarship Decision — {scholarship_name}",
        "Dear {student_name},\\n\\nThank you for your outstanding application to the {scholarship_name} scholarship.\\n\\nYour application has been reviewed and scored:\\n- Composite Score: {composite_score}/100\\n- Ranking: {ranking_tier}\\n- Committee Recommendation: {recommendation}\\n\\nEssay Evaluation:\\n{essay_evaluation_summary}\\n\\nStrengths: {essay_strengths}\\n\\nIf you are selected as a recipient (award amount: ${award_amount}), disbursement will be applied directly to your student account for the upcoming semester.\\n\\nFinal decisions will be communicated by the committee within 2 weeks.\\n\\nScholarship Committee",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // eligible -> collect recommendations
      e("4", "4a", "output_2"), // ineligible -> notify
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 797. SMALL CLAIMS COURT GUIDE BOT (7 nodes)
  // ===================================================================

  "Small Claims Court Guide Bot": {
    summary:
      "Self-service legal guidance workflow that assesses jurisdiction eligibility, provides step-by-step filing instructions, helps prepare documents, offers court preparation tips, and delivers a resource package. An inquiry triggers the flow, runCode assesses jurisdiction eligibility, ineligible claims are routed, AI generates a filing guide, a document preparation assistant follows, court preparation tips are provided, and a resource package is delivered via email.",
    nodes: [
      trigger("Webhook: Small claims inquiry received", "webhook_app", {
        source: "Legal Aid Website / Chatbot",
        event: "legal.small_claims_inquiry",
        howItStarts:
          "A person submits a small claims court question through the legal aid website, chatbot, or self-help portal",
        setup:
          "Connect your legal self-help portal or chatbot to send inquiry webhooks. Payload should include the claimant details, claim description, amount, and jurisdiction information.",
        inputFields: [],
      }),

      // 2. Jurisdiction eligibility assessment (runCode)
      aiNode(
        "2",
        "AI: Parse claim details",
        280,
        "You are a legal intake specialist. Parse the small claims inquiry and extract all relevant details. Identify the claimant, claim type, amount, jurisdiction, and opposing party. Respond JSON: {claimant_name: string, claimant_email: string, claim_description: string, claim_amount: number, claim_type: string, state: string, county: string, opposing_party: string, incident_date: string, statute_of_limitations_years: number}",
        "Small claims inquiry: {webhook_payload}",
        [
          { field: "claimant_name", as: "claimant_name", type: "string" },
          { field: "claimant_email", as: "claimant_email", type: "string" },
          {
            field: "claim_description",
            as: "claim_description",
            type: "string",
          },
          { field: "claim_amount", as: "claim_amount", type: "number" },
          { field: "claim_type", as: "claim_type", type: "string" },
          { field: "state", as: "state", type: "string" },
          { field: "county", as: "county", type: "string" },
          { field: "opposing_party", as: "opposing_party", type: "string" },
          { field: "incident_date", as: "incident_date", type: "string" },
          {
            field: "statute_of_limitations_years",
            as: "statute_of_limitations_years",
            type: "number",
          },
        ],
      ),

      // 2b. Jurisdiction eligibility check (runCode)
      n("2b", "runCode", "Check jurisdiction eligibility", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Assess small claims eligibility by jurisdiction
const claimAmount = parseFloat(vars.claim_amount) || 0;
const state = (vars.state || "").toLowerCase();
const incidentDate = vars.incident_date || "";
const solYears = parseInt(vars.statute_of_limitations_years) || 3;

// Small claims limits vary by state (simplified lookup)
const stateLimit = {
  "california": 10000, "texas": 20000, "new york": 10000, "florida": 8000,
  "illinois": 10000, "ohio": 6000, "pennsylvania": 12000, "michigan": 6500,
  "georgia": 15000, "north carolina": 10000
};
const limit = stateLimit[state] || 10000;

let eligible = "yes";
const issues = [];

if (claimAmount > limit) {
  eligible = "no";
  issues.push("Claim amount $" + claimAmount + " exceeds " + vars.state + " small claims limit of $" + limit);
}

// Check statute of limitations
if (incidentDate) {
  const incident = new Date(incidentDate);
  const now = new Date();
  const yearsDiff = (now.getTime() - incident.getTime()) / (365.25 * 24 * 60 * 60 * 1000);
  if (yearsDiff > solYears) {
    eligible = "no";
    issues.push("Statute of limitations may have expired (" + Math.round(yearsDiff * 10) / 10 + " years since incident, limit is " + solYears + " years)");
  }
}

return {
  jurisdiction_eligible: eligible,
  small_claims_limit: limit,
  eligibility_issues: issues.length > 0 ? issues.join("; ") : "Claim is within jurisdiction limits",
  filing_fee_estimate: claimAmount <= 1500 ? 30 : claimAmount <= 5000 ? 50 : 75
};`,
          outputVariables: [
            { name: "jurisdiction_eligible", type: "string" },
            { name: "small_claims_limit", type: "number" },
            { name: "eligibility_issues", type: "string" },
            { name: "filing_fee_estimate", type: "number" },
          ],
        },
      }),

      // 3. Ineligible claim condition
      n("3", "condition", "Claim eligible for small claims?", 760, {
        conditionData: [
          {
            variable: "jurisdiction_eligible",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. Ineligible routing (False branch)
      emailNode(
        "3a",
        "Ineligible claim notification",
        1000,
        "{claimant_email}",
        "Small Claims Court — Eligibility Concern",
        "Dear {claimant_name},\\n\\nThank you for your inquiry about filing a small claims case.\\n\\nBased on our assessment, there may be an issue with your claim:\\n{eligibility_issues}\\n\\nThe small claims court limit in {state} is ${small_claims_limit}.\\n\\nOptions:\\n- If your claim exceeds the limit, you may file in regular civil court (consider consulting an attorney)\\n- You may reduce your claim amount to fit within small claims limits\\n- Contact your local court clerk for jurisdiction-specific guidance\\n\\nLegal Aid Resources: https://www.lawhelp.org\\n\\nThis is general information, not legal advice.",
        -220,
      ),

      // 4. Step-by-step filing guide (AI)
      aiNode(
        "4",
        "AI: Generate filing guide",
        1240,
        "You are a court self-help center specialist. Generate a clear, step-by-step small claims filing guide specific to the claimant jurisdiction. Include filing location, required forms, service of process requirements, hearing preparation, and timeline. Be specific to the state and county. Disclaimer: this is information, not legal advice. Respond JSON: {filing_guide: string, required_forms: string, filing_location: string, service_method: string, hearing_timeline: string, demand_letter_template: string}",
        "Claimant: {claimant_name}, State: {state}, County: {county}\\nClaim: {claim_description}\\nAmount: ${claim_amount} (Type: {claim_type})\\nOpposing Party: {opposing_party}\\nFiling Fee: ~${filing_fee_estimate}",
        [
          { field: "filing_guide", as: "filing_guide", type: "string" },
          { field: "required_forms", as: "required_forms", type: "string" },
          { field: "filing_location", as: "filing_location", type: "string" },
          { field: "service_method", as: "service_method", type: "string" },
          { field: "hearing_timeline", as: "hearing_timeline", type: "string" },
          {
            field: "demand_letter_template",
            as: "demand_letter_template",
            type: "string",
          },
        ],
      ),

      // 5. Document preparation assistant
      aiNode(
        "5",
        "AI: Document preparation assistant",
        1480,
        "You are a legal document preparation assistant. Help the claimant prepare the necessary documents for their small claims case. Generate a demand letter template, an evidence checklist, and a witness list template. Include tips for organizing evidence. Respond JSON: {demand_letter: string, evidence_checklist: string, witness_template: string, document_tips: string}",
        "Claimant: {claimant_name}\\nOpposing Party: {opposing_party}\\nClaim: {claim_description}\\nAmount: ${claim_amount}\\nIncident Date: {incident_date}\\nRequired Forms: {required_forms}",
        [
          { field: "demand_letter", as: "demand_letter", type: "string" },
          {
            field: "evidence_checklist",
            as: "evidence_checklist",
            type: "string",
          },
          { field: "witness_template", as: "witness_template", type: "string" },
          { field: "document_tips", as: "document_tips", type: "string" },
        ],
      ),

      // 6. Court date preparation tips
      aiNode(
        "6",
        "AI: Court preparation tips",
        1720,
        "You are a court preparation coach. Provide practical advice for appearing in small claims court. Include what to wear, how to present evidence, courtroom etiquette, what to expect, common mistakes to avoid, and how to handle the judge questions. Be encouraging and practical. Respond JSON: {court_prep_guide: string, presentation_tips: string, common_mistakes: string, what_to_bring: string}",
        "Claim type: {claim_type}, Amount: ${claim_amount}, State: {state}\\nFiling location: {filing_location}\\nHearing timeline: {hearing_timeline}",
        [
          { field: "court_prep_guide", as: "court_prep_guide", type: "string" },
          {
            field: "presentation_tips",
            as: "presentation_tips",
            type: "string",
          },
          { field: "common_mistakes", as: "common_mistakes", type: "string" },
          { field: "what_to_bring", as: "what_to_bring", type: "string" },
        ],
      ),

      // 7. Resource package delivery (email)
      emailNode(
        "7",
        "Deliver resource package email",
        1960,
        "{claimant_email}",
        "Your Small Claims Court Resource Package — {state} {county}",
        "Dear {claimant_name},\\n\\nHere is your complete small claims court resource package for your claim against {opposing_party} for ${claim_amount}.\\n\\n--- FILING GUIDE ---\\n{filing_guide}\\n\\nFiling Location: {filing_location}\\nRequired Forms: {required_forms}\\nEstimated Filing Fee: ${filing_fee_estimate}\\nService Method: {service_method}\\nExpected Timeline: {hearing_timeline}\\n\\n--- DEMAND LETTER TEMPLATE ---\\n{demand_letter}\\n\\n--- EVIDENCE CHECKLIST ---\\n{evidence_checklist}\\n\\n--- COURT PREPARATION ---\\n{court_prep_guide}\\n\\nWhat to Bring: {what_to_bring}\\n\\nCommon Mistakes to Avoid: {common_mistakes}\\n\\nDISCLAIMER: This is general legal information, not legal advice. For specific legal questions, consult an attorney or visit your local legal aid office.\\n\\nLegal Self-Help Center",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4", "output_1"), // eligible -> filing guide
      e("3", "3a", "output_2"), // ineligible -> notify
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 798. STUDENT ADMISSION INQUIRY BOT (8 nodes)
  // ===================================================================

  "Student Admission Inquiry Bot": {
    summary:
      "Conversational admissions bot that classifies intent, searches the program catalog, assesses eligibility, looks up application status, flags missing documents, connects to counselors, and enrolls leads in HubSpot CRM. An inquiry triggers the flow, AI classifies intent, the program catalog is searched, a personalized eligibility assessment is provided, application status is looked up, a missing document condition routes, an admissions counselor is connected, and the lead is enrolled in HubSpot for nurturing.",
    nodes: [
      trigger("Webhook: Admissions inquiry received", "webhook_app", {
        source: "University Website / Chat",
        event: "admissions.inquiry",
        howItStarts:
          "A prospective student submits an admissions inquiry through the university website, live chat, or inquiry form",
        setup:
          "Connect your admissions inquiry form or chatbot to send webhooks. Payload includes the student message, contact info, and any program interest selections.",
        inputFields: [],
      }),

      // 2. AI intent classification
      aiNode(
        "2",
        "AI: Classify admissions intent",
        280,
        "You are a university admissions assistant. Parse the prospective student inquiry and classify the intent. Common intents: program_info, application_status, requirements, financial_aid, campus_visit, deadline_info, transfer_credit. Extract student contact details and specific program interests regardless of format. Respond JSON: {student_name: string, student_email: string, student_phone: string, intent: string, program_interest: string, academic_level: string, current_education: string, gpa_reported: string, test_scores: string, specific_question: string, has_application: string, application_id: string}",
        "Admissions inquiry: {webhook_payload}",
        [
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "student_phone", as: "student_phone", type: "string" },
          { field: "intent", as: "intent", type: "string" },
          { field: "program_interest", as: "program_interest", type: "string" },
          { field: "academic_level", as: "academic_level", type: "string" },
          {
            field: "current_education",
            as: "current_education",
            type: "string",
          },
          { field: "gpa_reported", as: "gpa_reported", type: "string" },
          { field: "test_scores", as: "test_scores", type: "string" },
          {
            field: "specific_question",
            as: "specific_question",
            type: "string",
          },
          { field: "has_application", as: "has_application", type: "string" },
          { field: "application_id", as: "application_id", type: "string" },
        ],
      ),

      // 3. Program catalog search
      aiNode(
        "3",
        "AI: Search program catalog",
        520,
        "You are a university program advisor. Based on the student interest and academic level, provide detailed information about matching programs. Include admission requirements, curriculum highlights, career outcomes, tuition range, and application deadlines. Respond JSON: {program_name: string, program_description: string, admission_requirements: string, tuition_range: string, application_deadline: string, career_outcomes: string, program_url: string, program_match_score: number}",
        "Student: {student_name}, Level: {academic_level}\\nProgram Interest: {program_interest}\\nCurrent Education: {current_education}\\nGPA: {gpa_reported}, Tests: {test_scores}\\nSpecific Question: {specific_question}",
        [
          { field: "program_name", as: "program_name", type: "string" },
          {
            field: "program_description",
            as: "program_description",
            type: "string",
          },
          {
            field: "admission_requirements",
            as: "admission_requirements",
            type: "string",
          },
          { field: "tuition_range", as: "tuition_range", type: "string" },
          {
            field: "application_deadline",
            as: "application_deadline",
            type: "string",
          },
          { field: "career_outcomes", as: "career_outcomes", type: "string" },
          { field: "program_url", as: "program_url", type: "string" },
          {
            field: "program_match_score",
            as: "program_match_score",
            type: "number",
          },
        ],
      ),

      // 4. Personalized eligibility assessment
      aiNode(
        "4",
        "AI: Personalized eligibility assessment",
        760,
        "You are an admissions evaluator. Based on the student academic profile and program requirements, provide an honest eligibility assessment. Be encouraging but realistic. Identify strengths, gaps, and recommended actions. Respond JSON: {eligibility_assessment: string, strengths: string, gaps: string, recommended_actions: string, admission_likelihood: string, missing_documents_list: string, has_missing_docs: string}",
        "Student: {student_name}\\nGPA: {gpa_reported}, Tests: {test_scores}, Education: {current_education}\\nProgram: {program_name}\\nRequirements: {admission_requirements}\\nHas Application: {has_application}, App ID: {application_id}",
        [
          {
            field: "eligibility_assessment",
            as: "eligibility_assessment",
            type: "string",
          },
          { field: "strengths", as: "strengths", type: "string" },
          { field: "gaps", as: "gaps", type: "string" },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "admission_likelihood",
            as: "admission_likelihood",
            type: "string",
          },
          {
            field: "missing_documents_list",
            as: "missing_documents_list",
            type: "string",
          },
          { field: "has_missing_docs", as: "has_missing_docs", type: "string" },
        ],
      ),

      // 5. Application status lookup
      emailNode(
        "5",
        "Send personalized program information",
        1000,
        "{student_email}",
        "Your Personalized Admissions Guide — {program_name}",
        "Dear {student_name},\\n\\nThank you for your interest in {program_name}!\\n\\nProgram Overview:\\n{program_description}\\n\\nAdmission Requirements:\\n{admission_requirements}\\n\\nYour Eligibility Assessment:\\n{eligibility_assessment}\\n\\nStrengths: {strengths}\\nAreas to Address: {gaps}\\n\\nRecommended Next Steps:\\n{recommended_actions}\\n\\nKey Dates:\\n- Application Deadline: {application_deadline}\\n- Tuition Range: {tuition_range}\\n\\nLearn More: {program_url}\\n\\nCareer Outcomes:\\n{career_outcomes}",
      ),

      // 6. Missing document condition
      n("6", "condition", "Has missing documents?", 1240, {
        conditionData: [
          {
            variable: "has_missing_docs",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 6a. Missing document notification (True branch)
      emailNode(
        "6a",
        "Send missing document reminder",
        1480,
        "{student_email}",
        "Action Required: Missing Documents for Your Application",
        "Dear {student_name},\\n\\nTo complete your application for {program_name}, we still need the following:\\n\\n{missing_documents_list}\\n\\nSubmit documents here: https://admissions.university.edu/upload/{application_id}\\n\\nDeadline: {application_deadline}\\n\\nNeed help? Contact your admissions counselor or reply to this email.\\n\\nAdmissions Office",
        -220,
      ),

      // 7. Admissions counselor connection
      slackNode(
        "7",
        "Notify admissions counselor (Slack)",
        1720,
        "NEW ADMISSIONS LEAD\\n\\nStudent: {student_name} ({student_email})\\nProgram: {program_name}\\nIntent: {intent}\\nAdmission Likelihood: {admission_likelihood}\\nGPA: {gpa_reported} | Tests: {test_scores}\\nMissing Docs: {has_missing_docs} — {missing_documents_list}\\nRecommended Actions: {recommended_actions}\\n\\nPlease reach out within 24 hours for a personal connection.",
      ),

      // 8. CRM nurture enrollment (HubSpot)
      hubspotNode(
        "8",
        "Enroll prospect in HubSpot CRM",
        1960,
        "POST",
        "crm/v3/objects/contacts",
        [
          {
            key: "properties",
            value:
              '{"email":"{student_email}","firstname":"{student_name}","phone":"{student_phone}","program_interest":"{program_name}","academic_level":"{academic_level}","admission_likelihood":"{admission_likelihood}","lifecyclestage":"subscriber","hs_lead_status":"NEW"}',
          },
        ],
        [
          { name: "id", customName: "hubspot_contact_id", type: "string" },
          {
            name: "properties.lifecyclestage",
            customName: "lifecycle_stage",
            type: "string",
          },
        ],
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "6a", "output_1"), // missing docs -> send reminder
      e("6", "7", "output_2"), // no missing docs -> counselor
      e("6a", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 799. STUDENT ATTENDANCE TRACKER (8 nodes)
  // ===================================================================

  "Student Attendance Tracker": {
    summary:
      "Automated attendance monitoring workflow that analyzes absence patterns, triggers threshold-based alerts, notifies students and parents, alerts faculty, refers to support services, and maintains a dashboard. An attendance data event triggers the flow, runCode analyzes absence patterns, a threshold breach condition routes, an early warning email goes to the student, a parent/guardian notification follows, a faculty advisor gets a Slack alert, support services are referred, and the dashboard is updated in Sheets.",
    nodes: [
      trigger("Webhook: Attendance data received", "webhook_app", {
        source: "SIS / LMS",
        event: "attendance.recorded",
        howItStarts:
          "The Student Information System or LMS fires a webhook when attendance is recorded, flagging students with excessive absences",
        setup:
          "Connect your SIS (Banner, PeopleSoft, etc.) or LMS (Canvas, Blackboard) to send attendance webhooks. Payload should include student ID, course, absence count, and term details.",
        inputFields: [],
      }),

      // 2. Absence pattern analysis (runCode)
      aiNode(
        "2",
        "AI: Extract attendance data",
        280,
        "You are a student success data analyst. Parse the attendance webhook and extract student details, course information, absence counts, and any pattern indicators. Respond JSON: {student_name: string, student_email: string, student_id: string, parent_email: string, course_name: string, course_id: string, instructor_name: string, instructor_email: string, total_absences: number, consecutive_absences: number, term: string, total_class_sessions: number, absence_dates: string}",
        "Attendance data payload: {webhook_payload}",
        [
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "student_id", as: "student_id", type: "string" },
          { field: "parent_email", as: "parent_email", type: "string" },
          { field: "course_name", as: "course_name", type: "string" },
          { field: "course_id", as: "course_id", type: "string" },
          { field: "instructor_name", as: "instructor_name", type: "string" },
          { field: "instructor_email", as: "instructor_email", type: "string" },
          { field: "total_absences", as: "total_absences", type: "number" },
          {
            field: "consecutive_absences",
            as: "consecutive_absences",
            type: "number",
          },
          { field: "term", as: "term", type: "string" },
          {
            field: "total_class_sessions",
            as: "total_class_sessions",
            type: "number",
          },
          { field: "absence_dates", as: "absence_dates", type: "string" },
        ],
      ),

      // 2b. Pattern analysis (runCode)
      n("2b", "runCode", "Analyze absence patterns", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Analyze absence patterns and determine alert level
const totalAbsences = parseInt(vars.total_absences) || 0;
const consecutiveAbsences = parseInt(vars.consecutive_absences) || 0;
const totalSessions = parseInt(vars.total_class_sessions) || 30;
const absenceRate = totalSessions > 0 ? ((totalAbsences / totalSessions) * 100).toFixed(1) : "0";

let alertLevel = "none";
let riskCategory = "low";
let interventionRequired = "no";

// Threshold logic
if (totalAbsences >= 6 || consecutiveAbsences >= 3 || parseFloat(absenceRate) >= 20) {
  alertLevel = "critical";
  riskCategory = "high";
  interventionRequired = "yes";
} else if (totalAbsences >= 4 || consecutiveAbsences >= 2 || parseFloat(absenceRate) >= 15) {
  alertLevel = "warning";
  riskCategory = "medium";
  interventionRequired = "yes";
} else if (totalAbsences >= 2) {
  alertLevel = "monitor";
  riskCategory = "low";
}

const breachesThreshold = alertLevel === "critical" || alertLevel === "warning" ? "yes" : "no";

return {
  absence_rate: absenceRate,
  alert_level: alertLevel,
  risk_category: riskCategory,
  intervention_required: interventionRequired,
  breaches_threshold: breachesThreshold,
  pattern_summary: consecutiveAbsences >= 2
    ? "Consecutive absence pattern detected (" + consecutiveAbsences + " in a row)"
    : "Scattered absences (" + totalAbsences + " total, " + absenceRate + "% rate)"
};`,
          outputVariables: [
            { name: "absence_rate", type: "string" },
            { name: "alert_level", type: "string" },
            { name: "risk_category", type: "string" },
            { name: "intervention_required", type: "string" },
            { name: "breaches_threshold", type: "string" },
            { name: "pattern_summary", type: "string" },
          ],
        },
      }),

      // 3. Threshold breach condition
      n("3", "condition", "Breaches attendance threshold?", 760, {
        conditionData: [
          {
            variable: "breaches_threshold",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4. Early warning student email
      emailNode(
        "4",
        "Send early warning to student",
        1000,
        "{student_email}",
        "Attendance Alert — {course_name}",
        "Dear {student_name},\\n\\nThis is an attendance notification for {course_name} ({term}).\\n\\nYour Attendance Summary:\\n- Total Absences: {total_absences} of {total_class_sessions} sessions\\n- Absence Rate: {absence_rate}%\\n- Alert Level: {alert_level}\\n- Pattern: {pattern_summary}\\n\\nExcessive absences can impact your grade and financial aid status. If you are experiencing difficulties, please reach out:\\n\\n- Your instructor: {instructor_name} ({instructor_email})\\n- Student Success Center: success@university.edu\\n- Dean of Students: dos@university.edu\\n\\nWe want to help you succeed.\\n\\nStudent Success Team",
      ),

      // 5. Parent/guardian notification
      emailNode(
        "5",
        "Notify parent/guardian",
        1240,
        "{parent_email}",
        "Student Attendance Notification — {student_name}",
        "Dear Parent/Guardian,\\n\\nThis notice is regarding {student_name} (ID: {student_id}) attendance in {course_name}.\\n\\nAttendance Summary:\\n- Total Absences: {total_absences}\\n- Absence Rate: {absence_rate}%\\n- Risk Level: {risk_category}\\n- Pattern: {pattern_summary}\\n\\nWe encourage you to discuss attendance with your student. Continued absences may affect academic standing and financial aid.\\n\\nFor questions, contact the Student Success Center at success@university.edu.\\n\\nStudent Affairs",
      ),

      // 6. Faculty/advisor Slack alert
      slackNode(
        "6",
        "Alert faculty advisor (Slack)",
        1480,
        "ATTENDANCE ALERT [{alert_level}]\\n\\nStudent: {student_name} (ID: {student_id})\\nCourse: {course_name} ({course_id})\\nInstructor: {instructor_name}\\n\\nAbsences: {total_absences}/{total_class_sessions} ({absence_rate}%)\\nConsecutive: {consecutive_absences}\\nRisk: {risk_category}\\nPattern: {pattern_summary}\\n\\nIntervention required: {intervention_required}\\nDates: {absence_dates}",
      ),

      // 7. Support services referral
      emailNode(
        "7",
        "Refer to support services",
        1720,
        "success@university.edu",
        "Student Referral — {student_name} ({student_id}) — Attendance Concern",
        "Student Success Team,\\n\\nPlease initiate outreach for the following student:\\n\\nStudent: {student_name} (ID: {student_id})\\nEmail: {student_email}\\nCourse: {course_name} — {instructor_name}\\nAbsences: {total_absences} ({absence_rate}%)\\nAlert Level: {alert_level}\\nRisk Category: {risk_category}\\nPattern: {pattern_summary}\\n\\nRecommended Interventions:\\n- Academic advising meeting\\n- Tutoring assessment\\n- Wellness check (if consecutive absences)\\n- Financial aid review\\n\\nPlease document outreach in the student record.",
      ),

      // 8. Attendance dashboard (Sheets)
      sheetsNode(
        "8",
        "Update attendance dashboard (Sheets)",
        1960,
        "AttendanceTracker",
        '[["{student_id}","{student_name}","{course_name}","{term}","{total_absences}","{absence_rate}","{alert_level}","{risk_category}","{pattern_summary}","{intervention_required}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4", "output_1"), // threshold breached -> student warning
      e("3", "8", "output_2"), // no breach -> just log
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 800. SYMPTOM CHECKER PRE-SCREEN BOT (8 nodes)
  // ===================================================================

  "Symptom Checker Pre-Screen Bot": {
    summary:
      "Clinical pre-screening workflow that collects symptoms conversationally, detects emergency conditions, classifies triage acuity, delivers care recommendations, facilitates appointment booking, and documents for the EHR. A patient symptom submission triggers the flow, AI collects symptoms conversationally, runCode detects emergency symptoms, an emergency escalation condition routes critical cases, AI classifies triage acuity, care recommendations are delivered, an appointment is facilitated, and pre-visit documentation is prepared for the EHR.",
    nodes: [
      trigger("Webhook: Symptom report submitted", "webhook_app", {
        source: "Patient Portal / App",
        event: "symptoms.submitted",
        howItStarts:
          "A patient submits symptoms through the patient portal, mobile health app, or nurse triage line",
        setup:
          "Connect your patient-facing symptom checker or triage system to send webhooks. Payload includes the patient message describing symptoms, demographics, and any medical history context.",
        inputFields: [],
      }),

      // 2. AI conversational symptom collection
      aiNode(
        "2",
        "AI: Analyze reported symptoms",
        280,
        "You are a clinical triage nurse. Parse the patient symptom report and extract structured symptom data. Identify primary symptoms, duration, severity, associated symptoms, relevant medical history, medications, and demographics. Flag any emergency indicators (chest pain, difficulty breathing, stroke symptoms, severe bleeding, loss of consciousness). Respond JSON: {patient_name: string, patient_email: string, patient_phone: string, patient_age: number, primary_symptom: string, symptom_duration: string, severity_level: string, associated_symptoms: string, medical_history: string, current_medications: string, allergies: string, emergency_indicators: string, has_emergency_symptoms: string, symptom_onset: string, vital_signs_reported: string}",
        "Patient symptom report: {webhook_payload}",
        [
          { field: "patient_name", as: "patient_name", type: "string" },
          { field: "patient_email", as: "patient_email", type: "string" },
          { field: "patient_phone", as: "patient_phone", type: "string" },
          { field: "patient_age", as: "patient_age", type: "number" },
          { field: "primary_symptom", as: "primary_symptom", type: "string" },
          { field: "symptom_duration", as: "symptom_duration", type: "string" },
          { field: "severity_level", as: "severity_level", type: "string" },
          {
            field: "associated_symptoms",
            as: "associated_symptoms",
            type: "string",
          },
          { field: "medical_history", as: "medical_history", type: "string" },
          {
            field: "current_medications",
            as: "current_medications",
            type: "string",
          },
          { field: "allergies", as: "allergies", type: "string" },
          {
            field: "emergency_indicators",
            as: "emergency_indicators",
            type: "string",
          },
          {
            field: "has_emergency_symptoms",
            as: "has_emergency_symptoms",
            type: "string",
          },
          { field: "symptom_onset", as: "symptom_onset", type: "string" },
          {
            field: "vital_signs_reported",
            as: "vital_signs_reported",
            type: "string",
          },
        ],
      ),

      // 3. Emergency symptom detection (runCode)
      n("3", "runCode", "Detect emergency symptoms", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Detect emergency symptoms requiring immediate escalation
const hasEmergency = (vars.has_emergency_symptoms || "").toLowerCase();
const severity = (vars.severity_level || "").toLowerCase();
const emergencyIndicators = (vars.emergency_indicators || "").toLowerCase();
const age = parseInt(vars.patient_age) || 30;

let isEmergency = "no";
let emergencyReason = "No emergency indicators detected";

if (hasEmergency === "yes") {
  isEmergency = "yes";
  emergencyReason = "Emergency symptoms reported: " + vars.emergency_indicators;
} else if (severity === "severe" || severity === "critical") {
  isEmergency = "yes";
  emergencyReason = "Severity level is " + severity;
} else if (emergencyIndicators.includes("chest pain") || emergencyIndicators.includes("breathing") ||
           emergencyIndicators.includes("stroke") || emergencyIndicators.includes("unconscious")) {
  isEmergency = "yes";
  emergencyReason = "Emergency keyword detected: " + vars.emergency_indicators;
}

// Age-based risk escalation
if (age < 2 || age > 75) {
  if (severity === "moderate") {
    isEmergency = "yes";
    emergencyReason = "Moderate symptoms in high-risk age group (age: " + age + ")";
  }
}

return {
  is_emergency: isEmergency,
  emergency_reason: emergencyReason,
  escalation_type: isEmergency === "yes" ? "911_or_ED" : "routine"
};`,
          outputVariables: [
            { name: "is_emergency", type: "string" },
            { name: "emergency_reason", type: "string" },
            { name: "escalation_type", type: "string" },
          ],
        },
      }),

      // 4. Emergency escalation condition
      n("4", "condition", "Emergency symptoms detected?", 760, {
        conditionData: [
          {
            variable: "is_emergency",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Emergency escalation (True branch)
      smsNode(
        "4a",
        "EMERGENCY: Call 911 notification",
        1000,
        "{patient_phone}",
        "URGENT: {patient_name}, based on your symptoms ({emergency_indicators}), please call 911 or go to your nearest emergency room immediately. Do not wait. If you are experiencing chest pain, difficulty breathing, or stroke symptoms, every minute matters. Emergency reason: {emergency_reason}",
        -220,
      ),

      // 5. AI triage acuity classification
      aiNode(
        "5",
        "AI: Classify triage acuity",
        1240,
        "You are an experienced triage nurse using the Emergency Severity Index (ESI). Classify the patient acuity level: ESI-1 (resuscitation), ESI-2 (emergent), ESI-3 (urgent), ESI-4 (less urgent), ESI-5 (non-urgent). Consider symptom severity, duration, age, medical history, and expected resource needs. Recommend the appropriate care setting. Respond JSON: {esi_level: number, acuity_label: string, care_setting: string, time_to_care: string, clinical_reasoning: string, differential_considerations: string}",
        "Patient: {patient_name}, Age: {patient_age}\\nPrimary: {primary_symptom} ({severity_level}) for {symptom_duration}\\nAssociated: {associated_symptoms}\\nHistory: {medical_history}\\nMedications: {current_medications}\\nAllergies: {allergies}\\nOnset: {symptom_onset}\\nVitals: {vital_signs_reported}",
        [
          { field: "esi_level", as: "esi_level", type: "number" },
          { field: "acuity_label", as: "acuity_label", type: "string" },
          { field: "care_setting", as: "care_setting", type: "string" },
          { field: "time_to_care", as: "time_to_care", type: "string" },
          {
            field: "clinical_reasoning",
            as: "clinical_reasoning",
            type: "string",
          },
          {
            field: "differential_considerations",
            as: "differential_considerations",
            type: "string",
          },
        ],
      ),

      // 6. Care recommendation delivery
      emailNode(
        "6",
        "Deliver care recommendations",
        1480,
        "{patient_email}",
        "Your Health Assessment Results — {primary_symptom}",
        "Dear {patient_name},\\n\\nBased on your reported symptoms, here is your pre-screening assessment:\\n\\nPrimary Concern: {primary_symptom}\\nSeverity: {severity_level}\\nDuration: {symptom_duration}\\n\\nAssessment: {acuity_label} (ESI Level {esi_level})\\nRecommended Care: {care_setting}\\nSuggested Timeframe: {time_to_care}\\n\\nClinical Notes: {clinical_reasoning}\\n\\nIMPORTANT: This is a pre-screening tool, not a diagnosis. Please seek professional medical care as recommended.\\n\\nTo schedule an appointment: https://patient-portal.health.org/schedule\\n\\nIf symptoms worsen, call 911 or visit your nearest emergency room.\\n\\nYour Healthcare Team",
      ),

      // 7. Appointment facilitation
      emailNode(
        "7",
        "Facilitate appointment booking",
        1720,
        "{patient_email}",
        "Schedule Your {care_setting} Visit — {patient_name}",
        "Dear {patient_name},\\n\\nBased on your symptom assessment, we recommend scheduling a {care_setting} visit within {time_to_care}.\\n\\nBook Now: https://patient-portal.health.org/schedule?reason={primary_symptom}&urgency={acuity_label}\\n\\nWhat to bring:\\n- Photo ID and insurance card\\n- List of current medications: {current_medications}\\n- Description of symptoms and timeline\\n\\nAllergies on file: {allergies}\\n\\nIf you need help scheduling, call us at 1-800-XXX-XXXX.\\n\\nYour Healthcare Team",
      ),

      // 8. Pre-visit EHR documentation
      sheetsNode(
        "8",
        "Document pre-visit in EHR log (Sheets)",
        1960,
        "TriagePreScreen",
        '[["{patient_name}","{patient_email}","{patient_age}","{primary_symptom}","{severity_level}","{symptom_duration}","{associated_symptoms}","{esi_level}","{acuity_label}","{care_setting}","{is_emergency}","{differential_considerations}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4a", "output_1"), // emergency -> immediate alert
      e("4", "5", "output_2"), // not emergency -> triage classification
      e("4a", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 801. TELEHEALTH SESSION PREP BOT (8 nodes)
  // ===================================================================

  "Telehealth Session Prep Bot": {
    summary:
      "Pre-session preparation workflow for telehealth that retrieves patient/appointment data, checks intake completion, handles incomplete intakes, verifies technology readiness, prepares the clinical package, sends session reminders with join links, and detects no-shows for recovery. A schedule trigger fires before the session, patient data is retrieved, runCode checks intake completion, incomplete intakes are routed, a tech readiness email is sent, the clinical preparation package is created, a session reminder with join link fires, and no-show detection triggers recovery.",
    nodes: [
      trigger("Schedule: Pre-session preparation", "schedule", {
        schedule: "2 hours before each scheduled telehealth appointment",
        howItStarts:
          "Runs on a schedule 2 hours before each telehealth appointment to prepare the patient and clinician for the session",
        setup:
          "Configure the schedule to fire 2 hours before each telehealth appointment. Connect to your EHR/scheduling system for appointment data retrieval.",
        inputFields: [],
      }),

      // 2. Patient/appointment data retrieval
      n("2", "hitApi", "EHR: Pull appointment details", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://api.ehr-system.com/v1/appointments/upcoming?type=telehealth&window=2h",
          headers: [
            { key: "Authorization", value: "Bearer {ehr_api_token}" },
            { key: "Accept", value: "application/json" },
          ],
          body: [],
          variables: [
            {
              name: "data[0].patient.name",
              customName: "patient_name",
              type: "string",
            },
            {
              name: "data[0].patient.email",
              customName: "patient_email",
              type: "string",
            },
            {
              name: "data[0].patient.phone",
              customName: "patient_phone",
              type: "string",
            },
            {
              name: "data[0].patient.id",
              customName: "patient_id",
              type: "string",
            },
            {
              name: "data[0].provider.name",
              customName: "provider_name",
              type: "string",
            },
            {
              name: "data[0].provider.email",
              customName: "provider_email",
              type: "string",
            },
            {
              name: "data[0].appointment_time",
              customName: "appointment_time",
              type: "string",
            },
            {
              name: "data[0].appointment_id",
              customName: "appointment_id",
              type: "string",
            },
            {
              name: "data[0].visit_reason",
              customName: "visit_reason",
              type: "string",
            },
            {
              name: "data[0].join_url",
              customName: "join_url",
              type: "string",
            },
            {
              name: "data[0].intake_form_status",
              customName: "intake_status",
              type: "string",
            },
            {
              name: "data[0].last_visit_notes",
              customName: "last_visit_notes",
              type: "string",
            },
          ],
        },
      }),

      // 3. Intake completion check (runCode)
      n("3", "runCode", "Check intake form completion", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if pre-visit intake is complete
const intakeStatus = (vars.intake_status || "").toLowerCase();
const isComplete = intakeStatus === "complete" || intakeStatus === "completed" || intakeStatus === "submitted";

return {
  intake_complete: isComplete ? "yes" : "no",
  intake_action_needed: isComplete ? "none" : "Patient must complete intake form before session",
  session_ready: isComplete ? "yes" : "pending_intake"
};`,
          outputVariables: [
            { name: "intake_complete", type: "string" },
            { name: "intake_action_needed", type: "string" },
            { name: "session_ready", type: "string" },
          ],
        },
      }),

      // 4. Incomplete intake condition
      n("4", "condition", "Intake form complete?", 760, {
        conditionData: [
          {
            variable: "intake_complete",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Incomplete intake - send reminder (False branch)
      emailNode(
        "4a",
        "Send intake completion reminder",
        1000,
        "{patient_email}",
        "Action Required: Complete Your Intake Before Today's Telehealth Visit",
        "Dear {patient_name},\\n\\nYour telehealth appointment with {provider_name} is scheduled for {appointment_time}.\\n\\nPlease complete your pre-visit intake form before the session:\\nhttps://portal.health.org/intake/{appointment_id}\\n\\nThe form takes approximately 5-10 minutes and helps your provider prepare for your visit.\\n\\nVisit Reason: {visit_reason}\\n\\nIf you have already completed the form, please disregard this message.\\n\\nHealthcare Team",
        -220,
      ),

      // 5. Technology readiness check (email)
      emailNode(
        "5",
        "Send tech readiness check",
        1240,
        "{patient_email}",
        "Get Ready for Your Telehealth Visit — {appointment_time}",
        "Dear {patient_name},\\n\\nYour telehealth visit with {provider_name} is in 2 hours ({appointment_time}).\\n\\nTechnology Checklist:\\n- Test your camera and microphone\\n- Ensure stable internet connection (3+ Mbps recommended)\\n- Use Chrome, Firefox, or Safari browser\\n- Find a quiet, private space\\n- Charge your device fully\\n\\nTest your connection now: https://portal.health.org/tech-check\\n\\nJoin Link (save for later): {join_url}\\n\\nTrouble? Call our tech support at 1-800-XXX-XXXX ext. 3.",
      ),

      // 6. Clinical preparation package
      emailNode(
        "6",
        "Send clinical prep package to provider",
        1480,
        "{provider_email}",
        "Telehealth Prep: {patient_name} — {appointment_time}",
        "Dr. {provider_name},\\n\\nUpcoming Telehealth Session:\\n\\nPatient: {patient_name} (ID: {patient_id})\\nTime: {appointment_time}\\nReason: {visit_reason}\\nIntake: {intake_complete}\\n\\nLast Visit Notes:\\n{last_visit_notes}\\n\\nJoin Link: {join_url}\\n\\nPlease review the patient chart before the session.\\n\\nCare Coordination",
      ),

      // 7. Session reminder with join link
      smsNode(
        "7",
        "Send session reminder SMS with join link",
        1720,
        "{patient_phone}",
        "Reminder: Your telehealth visit with {provider_name} is at {appointment_time}. Join here: {join_url} — Find a quiet, private space with good internet. Questions? Call 1-800-XXX-XXXX.",
      ),

      // 8. No-show detection + recovery
      n("8", "delayAction", "Wait for session start + 15 min", 1960, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "135",
          delayUnit: "minutes",
        },
      }),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // intake complete -> tech readiness
      e("4", "4a", "output_2"), // incomplete -> send reminder
      e("4a", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 802. TENANT MAINTENANCE REQUEST BOT (9 nodes)
  // ===================================================================

  "Tenant Maintenance Request Bot": {
    summary:
      "End-to-end maintenance request processing workflow that classifies issues with AI, detects emergencies, checks tenant responsibility, creates work orders, assigns vendors, communicates with tenants, verifies completion, and escalates unresolved issues. A request triggers the flow, AI classifies the issue, an emergency condition routes urgent cases, tenant responsibility is checked, a work order is created, a vendor is assigned, a tenant communication email is sent, completion is verified, and unresolved issues are escalated via Slack.",
    nodes: [
      trigger("Webhook: Maintenance request submitted", "webhook_app", {
        source: "Tenant Portal / App",
        event: "maintenance.request_submitted",
        howItStarts:
          "A tenant submits a maintenance request through the property management portal, tenant app, or phone line",
        setup:
          "Connect your property management system (AppFolio, Buildium, RentManager) to send maintenance request webhooks. Payload should include tenant info, unit, issue description, and any uploaded photos.",
        inputFields: [],
      }),

      // 2. AI issue classification
      aiNode(
        "2",
        "AI: Classify maintenance issue",
        280,
        "You are a property maintenance specialist. Analyze the maintenance request and classify the issue by category (plumbing, electrical, HVAC, appliance, structural, pest, cosmetic, safety), priority (emergency, urgent, routine, low), and responsible party (landlord, tenant). Emergency includes: water leak, no heat in winter, gas smell, electrical hazard, fire damage, broken lock. Respond JSON: {tenant_name: string, tenant_email: string, tenant_phone: string, unit_number: string, property_address: string, issue_description: string, issue_category: string, priority: string, is_emergency: string, responsible_party: string, estimated_cost_range: string, requires_entry: string, photos_attached: string, lease_end_date: string}",
        "Maintenance request: {webhook_payload}",
        [
          { field: "tenant_name", as: "tenant_name", type: "string" },
          { field: "tenant_email", as: "tenant_email", type: "string" },
          { field: "tenant_phone", as: "tenant_phone", type: "string" },
          { field: "unit_number", as: "unit_number", type: "string" },
          { field: "property_address", as: "property_address", type: "string" },
          {
            field: "issue_description",
            as: "issue_description",
            type: "string",
          },
          { field: "issue_category", as: "issue_category", type: "string" },
          { field: "priority", as: "priority", type: "string" },
          { field: "is_emergency", as: "is_emergency", type: "string" },
          {
            field: "responsible_party",
            as: "responsible_party",
            type: "string",
          },
          {
            field: "estimated_cost_range",
            as: "estimated_cost_range",
            type: "string",
          },
          { field: "requires_entry", as: "requires_entry", type: "string" },
          { field: "photos_attached", as: "photos_attached", type: "string" },
          { field: "lease_end_date", as: "lease_end_date", type: "string" },
        ],
      ),

      // 3. Emergency detection condition
      n("3", "condition", "Is this an emergency?", 520, {
        conditionData: [
          {
            variable: "is_emergency",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. Emergency Slack alert (True branch)
      slackNode(
        "3a",
        "EMERGENCY maintenance alert (Slack)",
        760,
        "EMERGENCY MAINTENANCE REQUEST\\n\\nTenant: {tenant_name} ({tenant_phone})\\nUnit: {unit_number} at {property_address}\\nIssue: {issue_description}\\nCategory: {issue_category}\\nPriority: {priority}\\n\\nIMMEDIATE ACTION REQUIRED\\nCall the on-call maintenance team now. Respond within 1 hour per lease agreement.",
        -220,
      ),

      // 4. Tenant responsibility check
      aiNode(
        "4",
        "AI: Determine responsibility + work order details",
        1000,
        "You are a property management work order specialist. Based on the issue classification and typical lease terms, determine the responsible party, appropriate vendor type, and work order details. If the issue is tenant-caused damage, note the cost responsibility. Generate a work order with specifics. Respond JSON: {work_order_id: string, vendor_type: string, vendor_specialty: string, estimated_completion: string, tenant_cost_responsibility: string, entry_permission_needed: string, work_order_notes: string, parts_needed: string}",
        "Unit: {unit_number} at {property_address}\\nTenant: {tenant_name} (Lease ends: {lease_end_date})\\nIssue: {issue_description}\\nCategory: {issue_category}, Priority: {priority}\\nResponsible Party: {responsible_party}\\nPhotos: {photos_attached}\\nRequires Entry: {requires_entry}\\nEstimated Cost: {estimated_cost_range}",
        [
          { field: "work_order_id", as: "work_order_id", type: "string" },
          { field: "vendor_type", as: "vendor_type", type: "string" },
          { field: "vendor_specialty", as: "vendor_specialty", type: "string" },
          {
            field: "estimated_completion",
            as: "estimated_completion",
            type: "string",
          },
          {
            field: "tenant_cost_responsibility",
            as: "tenant_cost_responsibility",
            type: "string",
          },
          {
            field: "entry_permission_needed",
            as: "entry_permission_needed",
            type: "string",
          },
          { field: "work_order_notes", as: "work_order_notes", type: "string" },
          { field: "parts_needed", as: "parts_needed", type: "string" },
        ],
      ),

      // 5. Work order creation
      sheetsNode(
        "5",
        "Create work order (Sheets)",
        1240,
        "WorkOrders",
        '[["{work_order_id}","{tenant_name}","{unit_number}","{property_address}","{issue_category}","{priority}","{issue_description}","{vendor_type}","{estimated_cost_range}","{estimated_completion}","open"]]',
      ),

      // 6. Vendor/maintenance assignment
      slackNode(
        "6",
        "Assign vendor/maintenance team (Slack)",
        1480,
        "NEW WORK ORDER: {work_order_id}\\n\\nUnit: {unit_number} at {property_address}\\nCategory: {issue_category} ({priority})\\nIssue: {issue_description}\\n\\nVendor Type Needed: {vendor_type} — {vendor_specialty}\\nParts: {parts_needed}\\nEntry Required: {entry_permission_needed}\\nEstimated Completion: {estimated_completion}\\nCost Range: {estimated_cost_range}\\n\\nNotes: {work_order_notes}",
      ),

      // 7. Tenant communication email
      emailNode(
        "7",
        "Send tenant confirmation + status",
        1720,
        "{tenant_email}",
        "Maintenance Request Received — Work Order {work_order_id}",
        "Dear {tenant_name},\\n\\nYour maintenance request has been received and assigned.\\n\\nWork Order: {work_order_id}\\nUnit: {unit_number}\\nIssue: {issue_description}\\nCategory: {issue_category}\\nPriority: {priority}\\nEstimated Completion: {estimated_completion}\\n\\nA {vendor_type} specialist will be assigned to address this issue.\\n{entry_permission_needed}\\n\\nCost Responsibility: {tenant_cost_responsibility}\\n\\nTrack your request: https://portal.property.com/maintenance/{work_order_id}\\n\\nProperty Management Team",
      ),

      // 8. Completion verification (delay)
      n("8", "delayAction", "Wait for estimated completion", 1960, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "3",
          delayUnit: "days",
        },
      }),

      // 9. Unresolved issue escalation (Slack)
      slackNode(
        "9",
        "Escalate unresolved maintenance (Slack)",
        2200,
        "MAINTENANCE FOLLOW-UP NEEDED\\n\\nWork Order: {work_order_id} may still be open.\\nUnit: {unit_number} at {property_address}\\nTenant: {tenant_name} ({tenant_email})\\nIssue: {issue_description}\\nOriginal Priority: {priority}\\nEstimated Completion Was: {estimated_completion}\\n\\nPlease verify completion status and update the work order. Contact tenant if unresolved.",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "3a", "output_1"), // emergency -> immediate Slack alert
      e("3", "4", "output_2"), // not emergency -> standard flow
      e("3a", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },

  // ===================================================================
  // 803. TENANT MOVE-IN CHECKLIST BOT (8 nodes)
  // ===================================================================

  "Tenant Move-In Checklist Bot": {
    summary:
      "Automated move-in coordination workflow that checks unit turnover status, handles incomplete turnovers, sends utility transfer instructions, delivers document packages, schedules inspections, verifies renter's insurance, and coordinates move-in day logistics. A lease execution triggers the flow, runCode checks turnover status, an incomplete condition routes, utility transfer instructions are emailed, a move-in document package follows, an inspection is scheduled, renter's insurance is verified, and a day-of coordination email is sent.",
    nodes: [
      trigger("Webhook: Lease executed", "webhook_app", {
        source: "Property Management System",
        event: "lease.executed",
        howItStarts:
          "The property management system fires a webhook when a new lease is fully executed (signed by all parties)",
        setup:
          "Connect your property management system to send webhooks when leases are signed. Payload should include tenant details, unit info, lease terms, and move-in date.",
        inputFields: [],
      }),

      // 2. AI data extraction + unit turnover status check (runCode)
      aiNode(
        "2",
        "AI: Extract lease and unit details",
        280,
        "You are a property management coordinator. Parse the lease execution data and extract tenant details, unit information, lease terms, and move-in logistics. Respond JSON: {tenant_name: string, tenant_email: string, tenant_phone: string, unit_number: string, property_address: string, monthly_rent: number, lease_start_date: string, lease_end_date: string, move_in_date: string, security_deposit: number, unit_turnover_status: string, previous_tenant_moved_out: string, property_manager_email: string, parking_assignment: string, key_pickup_location: string}",
        "Lease execution payload: {webhook_payload}",
        [
          { field: "tenant_name", as: "tenant_name", type: "string" },
          { field: "tenant_email", as: "tenant_email", type: "string" },
          { field: "tenant_phone", as: "tenant_phone", type: "string" },
          { field: "unit_number", as: "unit_number", type: "string" },
          { field: "property_address", as: "property_address", type: "string" },
          { field: "monthly_rent", as: "monthly_rent", type: "number" },
          { field: "lease_start_date", as: "lease_start_date", type: "string" },
          { field: "lease_end_date", as: "lease_end_date", type: "string" },
          { field: "move_in_date", as: "move_in_date", type: "string" },
          { field: "security_deposit", as: "security_deposit", type: "number" },
          {
            field: "unit_turnover_status",
            as: "unit_turnover_status",
            type: "string",
          },
          {
            field: "previous_tenant_moved_out",
            as: "previous_tenant_moved_out",
            type: "string",
          },
          {
            field: "property_manager_email",
            as: "property_manager_email",
            type: "string",
          },
          {
            field: "parking_assignment",
            as: "parking_assignment",
            type: "string",
          },
          {
            field: "key_pickup_location",
            as: "key_pickup_location",
            type: "string",
          },
        ],
      ),

      // 2b. Unit turnover status check (runCode)
      n("2b", "runCode", "Check unit turnover readiness", 520, {
        runCodeDetails: {
          language: "javascript",
          code: `// Check if unit turnover is complete and ready for move-in
const turnoverStatus = (vars.unit_turnover_status || "").toLowerCase();
const prevMoveOut = (vars.previous_tenant_moved_out || "").toLowerCase();

const isReady = turnoverStatus === "complete" || turnoverStatus === "ready" || turnoverStatus === "clean";
const prevTenantOut = prevMoveOut === "yes" || prevMoveOut === "true" || prevMoveOut === "complete";

let readyForMoveIn = "yes";
const issues = [];

if (!isReady) issues.push("Unit turnover not complete (status: " + vars.unit_turnover_status + ")");
if (!prevTenantOut) issues.push("Previous tenant may not have fully moved out");

if (issues.length > 0) readyForMoveIn = "no";

return {
  unit_ready: readyForMoveIn,
  turnover_issues: issues.length > 0 ? issues.join("; ") : "Unit ready for move-in",
  needs_turnover_attention: readyForMoveIn === "no" ? "yes" : "no"
};`,
          outputVariables: [
            { name: "unit_ready", type: "string" },
            { name: "turnover_issues", type: "string" },
            { name: "needs_turnover_attention", type: "string" },
          ],
        },
      }),

      // 3. Turnover incomplete condition
      n("3", "condition", "Unit ready for move-in?", 760, {
        conditionData: [
          {
            variable: "unit_ready",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. Turnover incomplete - alert PM (False branch)
      slackNode(
        "3a",
        "Alert PM: Unit turnover incomplete (Slack)",
        1000,
        "MOVE-IN BLOCKED: Unit turnover not complete\\n\\nUnit: {unit_number} at {property_address}\\nNew Tenant: {tenant_name}\\nMove-in Date: {move_in_date}\\nIssues: {turnover_issues}\\n\\nPlease resolve immediately — tenant is expecting to move in on {move_in_date}.",
        -220,
      ),

      // 4. Utility transfer instructions (email)
      emailNode(
        "4",
        "Send utility transfer instructions",
        1240,
        "{tenant_email}",
        "Action Required: Set Up Utilities Before Move-In — {unit_number}",
        "Dear {tenant_name},\\n\\nCongratulations on your new home at {property_address}, Unit {unit_number}!\\n\\nPlease set up the following utilities in your name before {move_in_date}:\\n\\n1. ELECTRIC: Contact the local utility company, reference Unit {unit_number}\\n2. GAS: Contact the local gas provider (if applicable)\\n3. INTERNET: Schedule installation for move-in day or after\\n4. RENTER'S INSURANCE: Required — see details below\\n\\nAccount transfer must be effective on or before {lease_start_date} to avoid charges.\\n\\nUtility contacts for your area: https://portal.property.com/utilities/{property_address}\\n\\nProperty Management Team",
      ),

      // 5. Move-in document package
      emailNode(
        "5",
        "Send move-in document package",
        1480,
        "{tenant_email}",
        "Your Move-In Package — {property_address} Unit {unit_number}",
        "Dear {tenant_name},\\n\\nHere is your complete move-in package:\\n\\nLease Summary:\\n- Lease Period: {lease_start_date} to {lease_end_date}\\n- Monthly Rent: ${monthly_rent} (due on the 1st)\\n- Security Deposit: ${security_deposit}\\n- Parking: {parking_assignment}\\n\\nDocuments Included:\\n- Signed lease agreement\\n- Community rules and regulations\\n- Emergency contact list\\n- Maintenance request procedures\\n- Trash/recycling schedule\\n- Parking rules\\n\\nDownload all documents: https://portal.property.com/docs/{tenant_email}\\n\\nKey Pickup: {key_pickup_location} on {move_in_date}",
      ),

      // 6. Move-in inspection scheduling
      emailNode(
        "6",
        "Schedule move-in inspection",
        1720,
        "{tenant_email}",
        "Move-In Inspection — {move_in_date}",
        "Dear {tenant_name},\\n\\nA move-in inspection is scheduled for {move_in_date} at your unit.\\n\\nDuring the inspection, we will:\\n- Walk through the unit together\\n- Document the condition of all rooms, appliances, and fixtures\\n- Note any pre-existing damage on the inspection form\\n- Test all appliances, plumbing, and electrical\\n\\nPlease take your own photos during the walkthrough for your records.\\n\\nSign the inspection report: https://portal.property.com/inspection/{unit_number}\\n\\nThis protects your security deposit at move-out.\\n\\nProperty Management",
      ),

      // 7. Renter's insurance verification
      emailNode(
        "7",
        "Verify renter's insurance requirement",
        1960,
        "{tenant_email}",
        "Renter's Insurance Required — Due Before {move_in_date}",
        "Dear {tenant_name},\\n\\nPer your lease agreement, renter's insurance is required before move-in.\\n\\nMinimum Requirements:\\n- $100,000 liability coverage\\n- {property_address} Property Management listed as interested party\\n- Policy effective date: {lease_start_date}\\n\\nUpload your proof of insurance: https://portal.property.com/insurance/{tenant_email}\\n\\nNeed insurance? Get a quote in minutes:\\n- Lemonade: lemonade.com\\n- State Farm: statefarm.com\\n- Progressive: progressive.com\\n\\nInsurance must be on file before key pickup.\\n\\nProperty Management",
      ),

      // 8. Day-of coordination email
      emailNode(
        "8",
        "Send day-of move-in coordination",
        2200,
        "{tenant_email}",
        "Move-In Day Guide — Welcome Home, {tenant_name}!",
        "Dear {tenant_name},\\n\\nToday is the day! Here is your move-in guide:\\n\\nKey Pickup: {key_pickup_location}\\nUnit: {unit_number} at {property_address}\\nParking: {parking_assignment}\\n\\nMove-In Day Checklist:\\n1. Pick up keys at {key_pickup_location}\\n2. Complete the move-in inspection walk-through\\n3. Sign the inspection report\\n4. Test all appliances and utilities\\n5. Set up your mail forwarding\\n\\nEmergency Maintenance: 1-800-XXX-XXXX (24/7)\\nProperty Manager: {property_manager_email}\\n\\nWelcome to your new home!\\nProperty Management Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "2b"),
      e("2b", "3"),
      e("3", "4", "output_1"), // ready -> utility instructions
      e("3", "3a", "output_2"), // not ready -> alert PM
      e("3a", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 804. TOUR BOOKING ASSISTANT (8 nodes)
  // ===================================================================

  "Tour Booking Assistant": {
    summary:
      "End-to-end tour booking workflow with catalog search, AI recommendations, availability/pricing confirmation, sold-out routing, Stripe payment processing, confirmation email, and post-tour feedback collection. A booking inquiry triggers the flow, the tour catalog is searched, AI generates personalized recommendations, availability and pricing are confirmed, a sold-out condition routes, Stripe processes the booking, a confirmation email is sent, and a delayed post-tour feedback request follows.",
    nodes: [
      trigger("Webhook: Tour booking inquiry", "webhook_app", {
        source: "Website / Travel Platform",
        event: "tour.booking_inquiry",
        howItStarts:
          "A traveler submits a tour booking inquiry through the website, travel agency platform, or tour marketplace",
        setup:
          "Connect your tour booking platform or website form to send webhooks. Payload includes traveler details, destination interest, dates, group size, and budget preferences.",
        inputFields: [],
      }),

      // 2. Tour catalog search
      aiNode(
        "2",
        "AI: Parse inquiry + search catalog",
        280,
        "You are a travel tour specialist. Parse the booking inquiry and extract traveler details, preferences, and travel plans. Match against available tour options. Respond JSON: {traveler_name: string, traveler_email: string, traveler_phone: string, destination: string, travel_dates: string, group_size: number, budget_range: string, interests: string, tour_type_preference: string, accessibility_needs: string, matched_tour_name: string, matched_tour_id: string, tour_duration: string, tour_price_per_person: number, tour_total_price: number, tour_availability: string, tour_highlights: string}",
        "Tour inquiry: {webhook_payload}",
        [
          { field: "traveler_name", as: "traveler_name", type: "string" },
          { field: "traveler_email", as: "traveler_email", type: "string" },
          { field: "traveler_phone", as: "traveler_phone", type: "string" },
          { field: "destination", as: "destination", type: "string" },
          { field: "travel_dates", as: "travel_dates", type: "string" },
          { field: "group_size", as: "group_size", type: "number" },
          { field: "budget_range", as: "budget_range", type: "string" },
          { field: "interests", as: "interests", type: "string" },
          {
            field: "tour_type_preference",
            as: "tour_type_preference",
            type: "string",
          },
          {
            field: "accessibility_needs",
            as: "accessibility_needs",
            type: "string",
          },
          {
            field: "matched_tour_name",
            as: "matched_tour_name",
            type: "string",
          },
          { field: "matched_tour_id", as: "matched_tour_id", type: "string" },
          { field: "tour_duration", as: "tour_duration", type: "string" },
          {
            field: "tour_price_per_person",
            as: "tour_price_per_person",
            type: "number",
          },
          { field: "tour_total_price", as: "tour_total_price", type: "number" },
          {
            field: "tour_availability",
            as: "tour_availability",
            type: "string",
          },
          { field: "tour_highlights", as: "tour_highlights", type: "string" },
        ],
      ),

      // 3. AI personalized recommendations
      aiNode(
        "3",
        "AI: Generate personalized recommendations",
        520,
        "You are a luxury travel advisor. Based on the traveler preferences and matched tour, create a compelling personalized recommendation. Include why this tour is perfect for them, what makes it special, insider tips, and what to pack. Suggest add-on experiences. Respond JSON: {recommendation_text: string, why_perfect_for_you: string, insider_tips: string, packing_suggestions: string, addon_experiences: string, urgency_message: string}",
        "Traveler: {traveler_name}, Group: {group_size}\\nDestination: {destination}, Dates: {travel_dates}\\nInterests: {interests}, Type: {tour_type_preference}\\nBudget: {budget_range}, Accessibility: {accessibility_needs}\\nMatched Tour: {matched_tour_name} ({tour_duration}, ${tour_price_per_person}/person)\\nHighlights: {tour_highlights}\\nAvailability: {tour_availability}",
        [
          {
            field: "recommendation_text",
            as: "recommendation_text",
            type: "string",
          },
          {
            field: "why_perfect_for_you",
            as: "why_perfect_for_you",
            type: "string",
          },
          { field: "insider_tips", as: "insider_tips", type: "string" },
          {
            field: "packing_suggestions",
            as: "packing_suggestions",
            type: "string",
          },
          {
            field: "addon_experiences",
            as: "addon_experiences",
            type: "string",
          },
          { field: "urgency_message", as: "urgency_message", type: "string" },
        ],
      ),

      // 4. Availability/pricing confirmation
      n("4", "condition", "Tour available?", 760, {
        conditionData: [
          {
            variable: "tour_availability",
            value: "available",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Sold out - offer alternatives (False branch)
      emailNode(
        "4a",
        "Tour sold out — offer alternatives",
        1000,
        "{traveler_email}",
        "Tour Update — {matched_tour_name} Availability",
        "Dear {traveler_name},\\n\\nThank you for your interest in {matched_tour_name} on {travel_dates}!\\n\\nUnfortunately, this specific date is currently sold out.\\n\\nAlternatives:\\n- Same tour, different dates: https://tours.company.com/tour/{matched_tour_id}?alt_dates=true\\n- Similar tours in {destination}: https://tours.company.com/destination/{destination}\\n- Join our waitlist for cancellation spots\\n\\n{urgency_message}\\n\\nWould you like us to check alternative dates? Reply to this email or call us.\\n\\nTravel Team",
        -220,
      ),

      // 5. Stripe secure booking
      stripeNode(
        "5",
        "Stripe: Process tour booking payment",
        1240,
        "POST",
        "charges",
        [
          { key: "amount", value: "{tour_total_price}00" },
          { key: "currency", value: "usd" },
          {
            key: "description",
            value:
              "{matched_tour_name} — {traveler_name} x{group_size} ({travel_dates})",
          },
          { key: "receipt_email", value: "{traveler_email}" },
          { key: "metadata[tour_id]", value: "{matched_tour_id}" },
          { key: "metadata[group_size]", value: "{group_size}" },
        ],
        [
          { name: "id", customName: "booking_charge_id", type: "string" },
          { name: "status", customName: "payment_status", type: "string" },
          { name: "receipt_url", customName: "receipt_url", type: "string" },
        ],
      ),

      // 6. Booking confirmation email
      emailNode(
        "6",
        "Send booking confirmation",
        1480,
        "{traveler_email}",
        "Booking Confirmed! {matched_tour_name} — {travel_dates}",
        "Dear {traveler_name},\\n\\nYour tour is booked! Here are your details:\\n\\nTour: {matched_tour_name}\\nDestination: {destination}\\nDates: {travel_dates}\\nDuration: {tour_duration}\\nGroup Size: {group_size}\\nTotal Paid: ${tour_total_price}\\n\\nBooking ID: {booking_charge_id}\\nReceipt: {receipt_url}\\n\\n{recommendation_text}\\n\\nPacking Tips: {packing_suggestions}\\n\\nAdd-On Experiences: {addon_experiences}\\n\\nInsider Tips: {insider_tips}\\n\\nManage your booking: https://tours.company.com/bookings/{booking_charge_id}\\n\\nWe cannot wait to host you!\\nTravel Team",
      ),

      // 7. Post-tour feedback (delay)
      n("7", "delayAction", "Wait until after tour completion", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "7",
          delayUnit: "days",
        },
      }),

      // 8. Post-tour feedback request
      emailNode(
        "8",
        "Collect post-tour feedback",
        1960,
        "{traveler_email}",
        "How Was Your {matched_tour_name} Experience?",
        "Dear {traveler_name},\\n\\nWe hope you had an incredible time on {matched_tour_name} in {destination}!\\n\\nYour feedback helps us create even better experiences:\\nhttps://tours.company.com/feedback/{booking_charge_id}\\n\\nShare your experience and receive 10% off your next booking!\\n\\nWe would also love if you shared photos on social media with #OurTours.\\n\\nBook your next adventure: https://tours.company.com/explore\\n\\nThank you for traveling with us!\\nTravel Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5", "output_1"), // available -> process payment
      e("4", "4a", "output_2"), // sold out -> alternatives
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 805. TRADEMARK MONITORING AGENT (8 nodes)
  // ===================================================================

  "Trademark Monitoring Agent": {
    summary:
      "Proactive trademark protection workflow that monitors trademark databases, scans web/domain registrations, checks marketplace listings, scores similarity with AI, routes by risk tier, sends high-risk Slack alerts with response packages, and maintains a monitoring dashboard. A daily schedule fires the monitoring, trademark databases are searched, SerpAPI scans domains/web, marketplace listings are scanned, AI scores similarity, a risk tier condition routes, high-risk alerts go to Slack with response packages, and the dashboard is updated in Sheets.",
    nodes: [
      trigger("Schedule: Daily trademark monitoring", "schedule", {
        schedule: "Daily at 6:00 AM",
        howItStarts:
          "Runs daily to monitor for potential trademark infringements across databases, web, domains, and marketplaces",
        setup:
          "Configure your trademark details (mark text, registration numbers, classes) in the workflow variables. Set up SerpAPI and marketplace API keys.",
        inputFields: [],
      }),

      // 2. Trademark database search
      n("2", "hitApi", "USPTO: Search trademark filings", 280, {
        hitApiDetails: {
          method: "GET",
          url: "https://tsdrapi.uspto.gov/ts/cd/casestatus/sn/{trademark_serial_number}/info.json",
          headers: [{ key: "Accept", value: "application/json" }],
          body: [],
          variables: [
            {
              name: "trademarkInfo.statusCode",
              customName: "trademark_status",
              type: "string",
            },
            {
              name: "trademarkInfo.markLiteralElements",
              customName: "registered_mark",
              type: "string",
            },
            {
              name: "trademarkInfo.classNumbers",
              customName: "class_numbers",
              type: "string",
            },
          ],
        },
      }),

      // 3. Domain/web monitoring (SerpAPI)
      n("3", "hitApi", "SerpAPI: Web and domain monitoring", 520, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?q={registered_mark}+-site:yourdomain.com&num=20&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "organic_results",
              customName: "web_results_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "total_web_matches",
              type: "number",
            },
          ],
        },
      }),

      // 4. Marketplace listing scan
      n("4", "hitApi", "Amazon: Marketplace listing scan", 760, {
        hitApiDetails: {
          method: "GET",
          url: "https://serpapi.com/search.json?engine=amazon&amazon_domain=amazon.com&k={registered_mark}&api_key={serpapi_key}",
          headers: [],
          body: [],
          variables: [
            {
              name: "results",
              customName: "marketplace_results_json",
              type: "string",
            },
            {
              name: "search_information.total_results",
              customName: "marketplace_match_count",
              type: "number",
            },
          ],
        },
      }),

      // 5. AI similarity scoring
      aiNode(
        "5",
        "AI: Score trademark similarity risks",
        1000,
        "You are a trademark protection specialist. Analyze the web search results and marketplace listings for potential trademark infringements. Score each finding by similarity to the registered mark (0-100), likelihood of confusion, and commercial impact. Classify risk as: critical (obvious infringement), high (likely infringement), medium (possible confusion), or low (coincidental). Identify the top threats. Respond JSON: {total_threats_found: number, critical_count: number, high_count: number, medium_count: number, top_threat_name: string, top_threat_url: string, top_threat_score: number, highest_risk_tier: string, infringement_summary: string, recommended_actions: string, cease_and_desist_needed: string}",
        "Registered Mark: {registered_mark} (Classes: {class_numbers}, Status: {trademark_status})\\nWeb Results ({total_web_matches} found): {web_results_json}\\nMarketplace Results ({marketplace_match_count} found): {marketplace_results_json}",
        [
          {
            field: "total_threats_found",
            as: "total_threats_found",
            type: "number",
          },
          { field: "critical_count", as: "critical_count", type: "number" },
          { field: "high_count", as: "high_count", type: "number" },
          { field: "medium_count", as: "medium_count", type: "number" },
          { field: "top_threat_name", as: "top_threat_name", type: "string" },
          { field: "top_threat_url", as: "top_threat_url", type: "string" },
          { field: "top_threat_score", as: "top_threat_score", type: "number" },
          {
            field: "highest_risk_tier",
            as: "highest_risk_tier",
            type: "string",
          },
          {
            field: "infringement_summary",
            as: "infringement_summary",
            type: "string",
          },
          {
            field: "recommended_actions",
            as: "recommended_actions",
            type: "string",
          },
          {
            field: "cease_and_desist_needed",
            as: "cease_and_desist_needed",
            type: "string",
          },
        ],
      ),

      // 6. Risk tier condition
      n("6", "condition", "High-risk threats detected?", 1240, {
        conditionData: [
          {
            variable: "highest_risk_tier",
            value: "low",
            comparison_operator: "NOT EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 7. High-risk Slack alert + response package
      slackNode(
        "7",
        "High-risk trademark alert (Slack)",
        1480,
        "TRADEMARK INFRINGEMENT ALERT\\n\\nRegistered Mark: {registered_mark} (Classes: {class_numbers})\\nRisk Level: {highest_risk_tier}\\n\\nThreats Found: {total_threats_found}\\n- Critical: {critical_count}\\n- High: {high_count}\\n- Medium: {medium_count}\\n\\nTop Threat: {top_threat_name} (Score: {top_threat_score}/100)\\nURL: {top_threat_url}\\n\\nSummary: {infringement_summary}\\n\\nRecommended Actions: {recommended_actions}\\nCease & Desist Needed: {cease_and_desist_needed}\\n\\nPlease review and take appropriate legal action.",
      ),

      // 8. Monitoring dashboard (Sheets)
      sheetsNode(
        "8",
        "Update monitoring dashboard (Sheets)",
        1720,
        "TrademarkMonitoring",
        '[["{registered_mark}","{class_numbers}","{trademark_status}","{total_web_matches}","{marketplace_match_count}","{total_threats_found}","{highest_risk_tier}","{top_threat_name}","{top_threat_score}","{cease_and_desist_needed}","{recommended_actions}"]]',
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "5"),
      e("5", "6"),
      e("6", "7", "output_1"), // threats detected -> Slack alert
      e("6", "8", "output_2"), // low/no threats -> just log
      e("7", "8"),
    ],
  },

  // ===================================================================
  // 806. TUTORING SESSION SCHEDULER (7 nodes)
  // ===================================================================

  "Tutoring Session Scheduler": {
    summary:
      "Automated tutoring session scheduling workflow that matches tutors, handles no-availability cases, books sessions with Google Calendar, sends pre-session materials, delivers session reminders, and collects post-session feedback. A scheduling request triggers the flow, AI matches a tutor, a no-tutor condition routes, a session is booked with Google Calendar sync, pre-session materials are emailed, a delay fires a session reminder, and a post-session follow-up collects feedback.",
    nodes: [
      trigger("Webhook: Tutoring request submitted", "webhook_app", {
        source: "Student Portal / LMS",
        event: "tutoring.session_request",
        howItStarts:
          "A student submits a tutoring session request through the student portal, LMS, or tutoring center website",
        setup:
          "Connect your tutoring management system or LMS to send webhooks. Payload should include student details, subject, preferred times, and any specific topics or concepts they need help with.",
        inputFields: [],
      }),

      // 2. Tutor matching
      aiNode(
        "2",
        "AI: Match tutor to student",
        280,
        "You are a tutoring center coordinator. Parse the tutoring request and match the student with an appropriate tutor based on subject, level, and availability. Consider tutor expertise, ratings, and scheduling. Respond JSON: {student_name: string, student_email: string, student_phone: string, subject: string, topic_details: string, academic_level: string, preferred_date: string, preferred_time: string, session_duration: string, tutor_matched: string, tutor_name: string, tutor_email: string, tutor_expertise: string, tutor_rating: number, match_confidence: string}",
        "Tutoring request: {webhook_payload}",
        [
          { field: "student_name", as: "student_name", type: "string" },
          { field: "student_email", as: "student_email", type: "string" },
          { field: "student_phone", as: "student_phone", type: "string" },
          { field: "subject", as: "subject", type: "string" },
          { field: "topic_details", as: "topic_details", type: "string" },
          { field: "academic_level", as: "academic_level", type: "string" },
          { field: "preferred_date", as: "preferred_date", type: "string" },
          { field: "preferred_time", as: "preferred_time", type: "string" },
          { field: "session_duration", as: "session_duration", type: "string" },
          { field: "tutor_matched", as: "tutor_matched", type: "string" },
          { field: "tutor_name", as: "tutor_name", type: "string" },
          { field: "tutor_email", as: "tutor_email", type: "string" },
          { field: "tutor_expertise", as: "tutor_expertise", type: "string" },
          { field: "tutor_rating", as: "tutor_rating", type: "number" },
          { field: "match_confidence", as: "match_confidence", type: "string" },
        ],
      ),

      // 3. No tutor condition
      n("3", "condition", "Tutor matched?", 520, {
        conditionData: [
          {
            variable: "tutor_matched",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 3a. No tutor - waitlist (False branch)
      emailNode(
        "3a",
        "No tutor available — waitlist notification",
        760,
        "{student_email}",
        "Tutoring Request Update — {subject}",
        "Dear {student_name},\\n\\nThank you for requesting tutoring in {subject} ({topic_details}).\\n\\nUnfortunately, no tutor is available for your preferred time ({preferred_date} at {preferred_time}).\\n\\nYou have been added to our waitlist and will be notified when a tutor becomes available.\\n\\nAlternatives:\\n- View available times: https://tutoring.edu/schedule?subject={subject}\\n- Drop-in tutoring hours: https://tutoring.edu/dropin\\n- Peer study groups: https://tutoring.edu/groups\\n\\nTutoring Center",
        -220,
      ),

      // 4. Session booking + Google Calendar sync
      n("4", "hitApi", "Book session + Google Calendar", 1000, {
        hitApiDetails: {
          method: "POST",
          url: "https://www.googleapis.com/calendar/v3/calendars/primary/events?sendUpdates=all",
          headers: [
            { key: "Authorization", value: "Bearer {google_token}" },
            { key: "Content-Type", value: "application/json" },
          ],
          body: [
            {
              key: "summary",
              value: "Tutoring: {subject} — {student_name} with {tutor_name}",
            },
            {
              key: "description",
              value:
                "Subject: {subject}\\nTopic: {topic_details}\\nLevel: {academic_level}\\nStudent: {student_name} ({student_email})\\nTutor: {tutor_name} ({tutor_email})",
            },
            {
              key: "start",
              value:
                '{"dateTime":"{preferred_date}T{preferred_time}:00","timeZone":"America/New_York"}',
            },
            {
              key: "end",
              value:
                '{"dateTime":"{preferred_date}T{preferred_time}:00","timeZone":"America/New_York"}',
            },
            {
              key: "attendees",
              value: '[{"email":"{student_email}"},{"email":"{tutor_email}"}]',
            },
            {
              key: "conferenceData",
              value: '{"createRequest":{"requestId":"tutor-{preferred_date}"}}',
            },
          ],
          variables: [
            { name: "id", customName: "calendar_event_id", type: "string" },
            { name: "hangoutLink", customName: "meeting_link", type: "string" },
            { name: "htmlLink", customName: "calendar_link", type: "string" },
          ],
        },
      }),

      // 5. Pre-session materials email
      emailNode(
        "5",
        "Send pre-session materials",
        1240,
        "{student_email}",
        "Tutoring Session Confirmed — {subject} with {tutor_name}",
        "Dear {student_name},\\n\\nYour tutoring session is confirmed!\\n\\nDetails:\\n- Subject: {subject}\\n- Topic: {topic_details}\\n- Tutor: {tutor_name} (Expertise: {tutor_expertise}, Rating: {tutor_rating}/5)\\n- Date: {preferred_date} at {preferred_time}\\n- Duration: {session_duration}\\n- Meeting Link: {meeting_link}\\n\\nTo Get the Most From Your Session:\\n1. Bring specific questions and problem sets\\n2. Review your class notes beforehand\\n3. Have a notebook ready for new concepts\\n4. Test your video/audio before the session\\n\\nAdd to Calendar: {calendar_link}\\n\\nTutoring Center",
      ),

      // 6. Session reminder (delay)
      n("6", "delayAction", "Wait until 1 hour before session", 1480, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "1",
          delayUnit: "days",
        },
      }),

      // 7. Post-session follow-up + feedback
      emailNode(
        "7",
        "Post-session follow-up + feedback",
        1720,
        "{student_email}",
        "How Was Your {subject} Tutoring Session?",
        "Dear {student_name},\\n\\nWe hope your tutoring session with {tutor_name} on {subject} was helpful!\\n\\nPlease share your feedback:\\nhttps://tutoring.edu/feedback?event={calendar_event_id}\\n\\nRate your experience:\\n- Did the tutor explain concepts clearly?\\n- Was the session focused on your needs?\\n- Would you book with this tutor again?\\n\\nNeed another session? Book now: https://tutoring.edu/schedule?subject={subject}&tutor={tutor_email}\\n\\nStudy resources for {subject}: https://tutoring.edu/resources/{subject}\\n\\nKeep up the great work!\\nTutoring Center",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4", "output_1"), // tutor matched -> book session
      e("3", "3a", "output_2"), // no tutor -> waitlist
      e("4", "5"),
      e("5", "6"),
      e("6", "7"),
    ],
  },

  // ===================================================================
  // 807. VOLUNTEER SIGNUP AND COORDINATOR BOT (9 nodes)
  // ===================================================================

  "Volunteer Signup and Coordinator Bot": {
    summary:
      "End-to-end volunteer management workflow that assesses skills and availability with AI, matches opportunities, gates background checks, registers and onboards volunteers, assigns shifts with confirmation emails, sends pre-shift reminders, delivers post-shift impact reports, and runs retention re-engagement campaigns. An inquiry triggers the flow, AI assesses skills and availability, opportunities are matched, a background check condition gates, registration and onboarding proceed, a shift assignment with confirmation email follows, a pre-shift reminder fires, a post-shift impact report is sent, and retention re-engagement campaigns are launched.",
    nodes: [
      trigger("Webhook: Volunteer inquiry submitted", "webhook_app", {
        source: "Volunteer Portal / Website",
        event: "volunteer.signup",
        howItStarts:
          "A prospective volunteer submits an interest form through the nonprofit website, VolunteerMatch, or volunteer management portal",
        setup:
          "Connect your volunteer management system (VolunteerHub, Galaxy Digital, or custom portal) to send webhooks. Payload should include volunteer demographics, skills, availability, and area of interest.",
        inputFields: [],
      }),

      // 2. AI skills/availability assessment
      aiNode(
        "2",
        "AI: Assess volunteer skills and availability",
        280,
        "You are a volunteer coordinator. Parse the volunteer inquiry and assess their skills, experience, availability, and interests. Match their profile to organizational needs. Determine if a background check is required based on the volunteer role (required for: youth programs, vulnerable populations, driving, financial handling). Respond JSON: {volunteer_name: string, volunteer_email: string, volunteer_phone: string, skills: string, experience_level: string, availability_days: string, availability_hours: string, interests: string, preferred_role: string, requires_background_check: string, background_check_reason: string, languages_spoken: string, has_transportation: string, emergency_contact: string}",
        "Volunteer inquiry: {webhook_payload}",
        [
          { field: "volunteer_name", as: "volunteer_name", type: "string" },
          { field: "volunteer_email", as: "volunteer_email", type: "string" },
          { field: "volunteer_phone", as: "volunteer_phone", type: "string" },
          { field: "skills", as: "skills", type: "string" },
          { field: "experience_level", as: "experience_level", type: "string" },
          {
            field: "availability_days",
            as: "availability_days",
            type: "string",
          },
          {
            field: "availability_hours",
            as: "availability_hours",
            type: "string",
          },
          { field: "interests", as: "interests", type: "string" },
          { field: "preferred_role", as: "preferred_role", type: "string" },
          {
            field: "requires_background_check",
            as: "requires_background_check",
            type: "string",
          },
          {
            field: "background_check_reason",
            as: "background_check_reason",
            type: "string",
          },
          { field: "languages_spoken", as: "languages_spoken", type: "string" },
          {
            field: "has_transportation",
            as: "has_transportation",
            type: "string",
          },
          {
            field: "emergency_contact",
            as: "emergency_contact",
            type: "string",
          },
        ],
      ),

      // 3. Opportunity matching
      aiNode(
        "3",
        "AI: Match volunteer opportunities",
        520,
        "You are a volunteer program manager. Based on the volunteer profile, match them with the best available opportunities. Consider skills alignment, schedule compatibility, and personal interests. Provide a ranked list of opportunities. Respond JSON: {matched_opportunity: string, opportunity_description: string, shift_date: string, shift_time: string, location: string, coordinator_name: string, coordinator_email: string, training_required: string, orientation_date: string, match_score: number, alternative_opportunities: string}",
        "Volunteer: {volunteer_name}\\nSkills: {skills} (Level: {experience_level})\\nAvailability: {availability_days}, {availability_hours}\\nInterests: {interests}\\nPreferred Role: {preferred_role}\\nLanguages: {languages_spoken}\\nTransportation: {has_transportation}",
        [
          {
            field: "matched_opportunity",
            as: "matched_opportunity",
            type: "string",
          },
          {
            field: "opportunity_description",
            as: "opportunity_description",
            type: "string",
          },
          { field: "shift_date", as: "shift_date", type: "string" },
          { field: "shift_time", as: "shift_time", type: "string" },
          { field: "location", as: "location", type: "string" },
          { field: "coordinator_name", as: "coordinator_name", type: "string" },
          {
            field: "coordinator_email",
            as: "coordinator_email",
            type: "string",
          },
          {
            field: "training_required",
            as: "training_required",
            type: "string",
          },
          { field: "orientation_date", as: "orientation_date", type: "string" },
          { field: "match_score", as: "match_score", type: "number" },
          {
            field: "alternative_opportunities",
            as: "alternative_opportunities",
            type: "string",
          },
        ],
      ),

      // 4. Background check condition
      n("4", "condition", "Background check required?", 760, {
        conditionData: [
          {
            variable: "requires_background_check",
            value: "yes",
            comparison_operator: "EQUAL TO",
            logical_operator: "AND",
          },
        ],
      }),

      // 4a. Background check gate (True branch)
      n("4a", "approvalGate", "Background check: Pending clearance", 1000, {
        approvalGateData: {
          message:
            "Background check required for volunteer placement.\\n\\nVolunteer: {volunteer_name} ({volunteer_email})\\nRole: {matched_opportunity}\\nReason for Check: {background_check_reason}\\n\\nPlease initiate the background check through your screening provider and approve once cleared.\\nSkills: {skills}\\nExperience: {experience_level}",
        },
      }),

      // 5. Volunteer registration + onboarding
      emailNode(
        "5",
        "Send registration + onboarding package",
        1240,
        "{volunteer_email}",
        "Welcome, {volunteer_name}! Your Volunteer Journey Starts Here",
        "Dear {volunteer_name},\\n\\nWelcome to our volunteer family! We are thrilled to have you.\\n\\nYour Matched Opportunity:\\n{matched_opportunity} — {opportunity_description}\\n\\nOnboarding Steps:\\n1. Complete your volunteer profile: https://volunteer.org/profile/{volunteer_email}\\n2. Review volunteer handbook: https://volunteer.org/handbook\\n3. Attend orientation: {orientation_date}\\n4. Training required: {training_required}\\n\\nYour Coordinator: {coordinator_name} ({coordinator_email})\\n\\nEmergency Contact on File: {emergency_contact}\\n\\nAlternative Opportunities Available: {alternative_opportunities}\\n\\nWe cannot wait to see the impact you will make!\\nVolunteer Coordination Team",
      ),

      // 6. Shift assignment + confirmation (email)
      emailNode(
        "6",
        "Confirm shift assignment",
        1480,
        "{volunteer_email}",
        "Shift Confirmed — {matched_opportunity} on {shift_date}",
        "Dear {volunteer_name},\\n\\nYour volunteer shift is confirmed!\\n\\nDetails:\\n- Opportunity: {matched_opportunity}\\n- Date: {shift_date}\\n- Time: {shift_time}\\n- Location: {location}\\n- Coordinator: {coordinator_name}\\n\\nWhat to Bring:\\n- Photo ID\\n- Comfortable clothing/shoes\\n- Water bottle\\n- Positive attitude!\\n\\nCan't make it? Please let us know 48 hours in advance: https://volunteer.org/reschedule/{volunteer_email}\\n\\nSee you there!",
      ),

      // 7. Pre-shift reminder (delay)
      n("7", "delayAction", "Wait until day before shift", 1720, {
        delayDetails: {
          delayType: "fixed",
          delayValue: "1",
          delayUnit: "days",
        },
      }),

      // 8. Post-shift impact report
      emailNode(
        "8",
        "Send post-shift impact report",
        1960,
        "{volunteer_email}",
        "Thank You! Your Impact Report — {matched_opportunity}",
        "Dear {volunteer_name},\\n\\nThank you for volunteering with {matched_opportunity} on {shift_date}!\\n\\nYour Impact:\\nEvery hour you give makes a real difference in our community. Your contribution of time and talent in {matched_opportunity} directly supports our mission.\\n\\nShare Your Experience:\\nhttps://volunteer.org/feedback/{volunteer_email}?date={shift_date}\\n\\nLog Your Hours: https://volunteer.org/hours/{volunteer_email}\\n\\nUpcoming Opportunities: {alternative_opportunities}\\n\\nRefer a Friend: https://volunteer.org/refer?from={volunteer_email}\\n\\nWith gratitude,\\n{coordinator_name}\\nVolunteer Coordination Team",
      ),

      // 9. Retention re-engagement
      emailNode(
        "9",
        "Retention re-engagement campaign",
        2200,
        "{volunteer_email}",
        "We Miss You, {volunteer_name} — New Opportunities Available!",
        "Dear {volunteer_name},\\n\\nIt has been a while since your last volunteer shift, and we wanted to check in!\\n\\nYour Skills Matter: {skills}\\nYour Impact Area: {matched_opportunity}\\n\\nNew opportunities that match your interests:\\n{alternative_opportunities}\\n\\nFlexible scheduling available: {availability_days}\\n\\nSign up for your next shift: https://volunteer.org/schedule?skills={skills}\\n\\nTell us what you need: https://volunteer.org/preferences/{volunteer_email}\\n\\nWhether you can give an hour or a day, your contribution matters.\\n\\nVolunteer Coordination Team",
      ),
    ],
    edges: [
      e("1", "2"),
      e("2", "3"),
      e("3", "4"),
      e("4", "4a", "output_1"), // background check required -> approval gate
      e("4", "5", "output_2"), // no background check -> register
      e("4a", "5"),
      e("5", "6"),
      e("6", "7"),
      e("7", "8"),
      e("8", "9"),
    ],
  },
};
